/*
 * TestHelden · Kognitive Tests im Shop (IQ-Test) · assets/th-cogtest.js
 * <th-cogtest>        Testablauf: Start → Blöcke mit Beispiel → Aufgaben → Angaben → Ergebnis
 * <th-cogtest-stats>  Community-Statistik (Karte, Geschlecht, Alter, Abschluss, Verteilung)
 * Aufgaben, Auswertung und Lösungen kommen von der Plattform (app.testhelden.com/api/mobile/iq-test/*);
 * der Browser bekommt keine Lösungen vor der Abgabe. Kein Tracking, keine Drittanbieter.
 * Version-Marker: data-thct-version (Prüfung, ob die Datei live angekommen ist).
 */
(() => {
  'use strict';
  if (window.customElements && customElements.get('th-cogtest')) return;
  const VERSION = '2026-10-21.2';
  const NF1 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1, minimumFractionDigits: 1 });
  const NF0 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
  const SVGNS = 'http://www.w3.org/2000/svg';
  const LETTERS = 'ABCDEFGH';

  // ── kleine Helfer ──────────────────────────────────────────────────────
  function h(tag, attrs, ...kids) {
    const e = document.createElement(tag);
    if (attrs) for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') e.className = v;
      else if (k === 'text') e.textContent = v;
      else if (k === 'html') e.innerHTML = v; // nur für SVG der eigenen Plattform
      else if (k.startsWith('on') && typeof v === 'function') e.addEventListener(k.slice(2), v);
      else e.setAttribute(k, v === true ? '' : String(v));
    }
    for (const kid of kids.flat()) if (kid !== null && kid !== undefined && kid !== false) e.append(kid.nodeType ? kid : document.createTextNode(String(kid)));
    return e;
  }
  function s(tag, attrs, ...kids) {
    const e = document.createElementNS(SVGNS, tag);
    if (attrs) for (const [k, v] of Object.entries(attrs)) if (v !== null && v !== undefined) e.setAttribute(k, String(v));
    for (const kid of kids.flat()) if (kid) e.append(kid.nodeType ? kid : document.createTextNode(String(kid)));
    return e;
  }
  const store = {
    get(k) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* privater Modus */ } },
    del(k) { try { localStorage.removeItem(k); } catch (e) { /* egal */ } },
  };
  const mmss = (sec) => { const t = Math.max(0, Math.ceil(sec)); return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0'); };
  const geraet = () => { const w = window.innerWidth; const coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches; return w < 600 ? 'mobil' : (w < 1024 && coarse) ? 'tablet' : 'desktop'; };
  const reduceMotion = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  function scrollIntoViewTop(el, sofort) {
    const y = el.getBoundingClientRect().top + window.scrollY - 88;
    if (Math.abs(window.scrollY - y) > 40) window.scrollTo({ top: Math.max(0, y), behavior: sofort || reduceMotion() ? 'auto' : 'smooth' });
  }
  // Element in den sichtbaren Bereich unter der festen Kopfzeile holen, nur wenn nötig (Abnahme 2, Opus N1: mit
  // scrollIntoView „nearest“ lag der Fokus bei 390 px unter der Kopfzeile)
  function inSicht(el) {
    const kopf = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const oben = Math.max(88, kopf + 16);
    const r = el.getBoundingClientRect();
    if (r.top >= oben && r.bottom <= window.innerHeight - 16) return;
    const platz = window.innerHeight - oben - 16;
    const versatz = r.height < platz ? oben + (platz - r.height) / 2 : oben;
    // sofort statt weich (die Seite hat scroll-behavior: smooth): Der Dialog lag davor, ein Gleiten ließe den Fokus
    // kurz außerhalb des Bildes (Abnahme 3, Opus: Zwischenwert nach 500 ms) und ruht in Hintergrund-Tabs ganz
    const wurzel = document.documentElement, war = wurzel.style.scrollBehavior;
    wurzel.style.scrollBehavior = 'auto';
    window.scrollTo(0, Math.max(0, window.scrollY + r.top - versatz));
    wurzel.style.scrollBehavior = war;
  }
  // Unter 18 auch bei Widerspruch und „Ohne Angaben“ (dort kennt der Server kein Alter): Server-Hinweis, sonst die
  // Angabe in diesem Browser (Fable D2, Opus N-H2)
  function istJung(r, run) {
    const angabe = run && run.angaben && run.angaben.alter ? Number(run.angaben.alter) : null;
    return r.minderjaehrig === true || (r.minderjaehrig !== false && ((r.alter !== null && r.alter !== undefined && r.alter < 18) || (angabe !== null && angabe < 18)));
  }
  async function api(base, path, opts) {
    const r = await fetch(base + path, Object.assign({ headers: { 'Content-Type': 'application/json' }, credentials: 'omit' }, opts));
    let body = null;
    try { body = await r.json(); } catch (e) { body = null; }
    if (!r.ok) {
      const err = new Error((body && body.error) || 'Verbindung fehlgeschlagen (' + r.status + ')');
      err.status = r.status;
      throw err;
    }
    return body;
  }
  const normalPdf = (x, m, sd) => Math.exp(-0.5 * ((x - m) / sd) ** 2) / (sd * Math.sqrt(2 * Math.PI));

  // ── Testablauf ─────────────────────────────────────────────────────────
  class ThCogtest extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.setAttribute('data-thct-version', VERSION);
      this.api = (this.dataset.api || 'https://app.testhelden.com').replace(/\/$/, '');
      this.instrument = this.dataset.instrument || 'th-iq';
      this.kRun = 'thct:' + this.instrument + ':lauf';
      this.kHist = 'thct:' + this.instrument + ':verlauf';
      this.testlauf = /[?&]testlauf=1(&|$)/.test(location.search);
      try { this.produkte = JSON.parse((this.querySelector('[data-thct-produkte]') || {}).textContent || '[]'); } catch (e) { this.produkte = []; }
      this.stage = this.querySelector('[data-thct-buehne]');
      // Startseite ohne den vorab gefüllten Hinweis merken (den setzt bindIntro() bei jedem Aufbau neu)
      const kopie = this.stage ? this.stage.cloneNode(true) : null;
      const vorab = kopie && kopie.querySelector('[data-thct-hinweis]');
      if (vorab) { vorab.replaceChildren(); vorab.hidden = true; }
      this.introHtml = kopie ? kopie.innerHTML : '';
      this.bindIntro();
    }

    // Startseite (serverseitig gerendert) verdrahten; offener Lauf → „Fortsetzen“ wird zum Hauptknopf.
    // Den Hinweis füllt ein Inline-Skript in sections/th-iq-test.liquid schon beim Aufbau der Seite mit denselben
    // Texten (CLS beim Neuladen, QA-Runde 3); hier wird er durch dieselben Elemente mit Funktion ersetzt.
    bindIntro() {
      const btn = this.stage.querySelector('[data-thct-start]');
      const run = store.get(this.kRun);
      const hist = store.get(this.kHist);
      const slot = this.stage.querySelector('[data-thct-hinweis]');
      const offen = run && run.session && Date.now() - run.startedAt < 2.5 * 3600e3 && !run.done;
      if (btn) {
        btn.disabled = false;
        if (offen) {
          btn.textContent = 'Test fortsetzen';
          btn.addEventListener('click', () => { this.stageHalten(); this.resume(run); });
        } else {
          btn.textContent = 'Test starten';
          btn.addEventListener('click', () => this.start());
        }
      }
      if (slot && offen) {
        slot.hidden = false;
        const neu = h('button', { type: 'button', class: 'thct-link', onclick: () => {
          neu.hidden = true;
          slot.append(h('p', { class: 'thct-klein thct-rueckfrage' }, 'Wirklich neu beginnen? Der angefangene Test wird verworfen. ',
            h('button', { type: 'button', class: 'thct-link', onclick: () => { store.del(this.kRun); this.neuStart(); } }, 'Ja, neu beginnen')));
        } }, 'Stattdessen neu beginnen');
        slot.replaceChildren(h('p', { text: 'Du hast einen Test angefangen und noch nicht abgeschlossen. Die Zeit des angefangenen Teils läuft weiter.' }), neu);
      } else if (slot && hist && hist.letztes) {
        const l = hist.letztes;
        slot.hidden = false;
        const zeigen = h('button', { type: 'button', class: 'thct-link', onclick: () => {
          zeigen.replaceWith(h('span', { text: 'vorläufige IQ-Schätzung ' + l.iq + ' (Bereich ' + l.ciLow + ' bis ' + l.ciHigh + ').' }));
        } }, 'anzeigen');
        slot.replaceChildren(h('p', null, 'Auf diesem Gerät gibt es ein Ergebnis vom ' + new Date(l.am).toLocaleDateString('de-DE') + ': ', zeigen, ' Ein neuer Durchlauf zählt als Wiederholung und fließt nicht in die Statistik ein.'));
      } else if (slot) {
        slot.hidden = true;
        slot.replaceChildren();
      }
    }

    // Layoutsprünge vermeiden (QA-Runde 2: CLS 0,15 nach dem Start, bis 0,9 beim Ergebnis): Ab dem Start hält die Bühne
    // mindestens ihre bisherige Höhe und die Fensterhöhe. Inhalte darunter liegen dann außerhalb des Bildschirms, wenn
    // Aufgaben oder Ergebnis nachladen. Synchron im Klick gesetzt (Verschiebungen direkt nach einer Eingabe zählen nicht).
    // Bei der Abgabe (faktor 2) zusätzlich sofort an den Anfang der Bühne scrollen: Am Handy stand man am Ende des
    // Angaben-Formulars, die Statistik darunter war sichtbar und rutschte beim Ergebnis (3000 px) nach unten, CLS 0,44
    // (QA-Runde 3, Opus M-5). Mit der Bühne oben und mindestens zwei Fensterhöhen liegt alles darunter außer Sicht.
    stageHalten(faktor) {
      if (!this.stage) return;
      if (faktor) scrollIntoViewTop(this, true);
      const hoehe = Math.ceil(Math.max(this.stage.getBoundingClientRect().height, (faktor || 1) * window.innerHeight));
      this.stage.style.minHeight = hoehe + 'px';
      this.stage.classList.add('is-laufend');
    }

    // Die gehaltene Höhe gilt nur fürs Laden. Steht echter Inhalt (Teil-Start, Aufgabe, Angaben), wird sie frei:
    // Sonst blieb die Höhe der längeren Startseite stehen, am Handy über 200 px leer unter „Teil 1 starten“ und
    // unter kurzen Aufgaben (Philipp 21.10.2026). Erst ohne Animation an den Anfang der Bühne, dann im nächsten
    // Bild lösen: So liegt der Inhalt darunter außer Sicht, wenn er nachrückt, und es entsteht kein Layoutsprung.
    stageZeigen() {
      const gehalten = !!(this.stage && this.stage.style.minHeight);
      scrollIntoViewTop(this, gehalten);
      if (gehalten) requestAnimationFrame(() => { this.stage.style.minHeight = ''; });
    }

    async start() {
      this.stageHalten();
      this.renderMessage('Aufgaben werden erstellt …', true);
      try {
        const session = await api(this.api, '/api/mobile/iq-test/start', { method: 'POST', body: JSON.stringify({ instrument: this.instrument, testlauf: this.testlauf }) });
        this.run = { session, startedAt: Date.now(), block: 0, item: -1, answers: {}, blockStart: {}, done: false };
        this.save();
        this.showBlockIntro();
      } catch (e) {
        this.renderError(e.message || 'Der Test konnte nicht starten.', () => this.start());
      }
    }

    guardOn() {
      if (this._guard) return;
      this._guard = true;
      try { history.pushState({ thct: 1 }, ''); } catch (e) { /* egal */ }
      this._onPop = () => {
        if (!this._guard) return;
        try { history.pushState({ thct: 1 }, ''); } catch (e) { /* egal */ }
        this.leaveDialog();
      };
      this._onUnload = (ev) => { if (this._guard) { ev.preventDefault(); ev.returnValue = ''; } };
      window.addEventListener('popstate', this._onPop);
      window.addEventListener('beforeunload', this._onUnload);
      // Zurück-Taste soll die Seite nicht verschieben (der Browser stellt sonst die Scrollposition des Eintrags her)
      try { this._scrollWar = history.scrollRestoration; history.scrollRestoration = 'manual'; } catch (e) { /* egal */ }
    }
    guardOff() {
      if (!this._guard) return;
      this._guard = false;
      window.removeEventListener('popstate', this._onPop);
      window.removeEventListener('beforeunload', this._onUnload);
      try { if (this._scrollWar) history.scrollRestoration = this._scrollWar; } catch (e) { /* egal */ }
      if (this._dialogAus) this._dialogAus();
      const offen = this.querySelector('.thct-verlassen');
      if (offen) offen.remove();
    }
    // Als festes Overlay im sichtbaren Bereich, egal wie weit die Seite gescrollt ist (QA-Runde 3, Fable N2: oben in
    // der Bühne lag der Dialog am Handy bis 630 px außerhalb des Bildschirms). Schließen gibt den Fokus an die Stelle
    // zurück, an der er vorher war (Fable N3: sonst lag er auf dem Artikel, Tasten wirkten nicht mehr).
    leaveDialog() {
      if (this.querySelector('.thct-verlassen')) return;
      const vorher = this.contains(document.activeElement) ? document.activeElement : null;
      // Fokus und Tasten bleiben im Dialog, auch wenn dahinter neu gezeichnet wird (Abnahme 3, Fable N1, hier z. B.
      // wenn das Zeitlimit eines Teils abläuft). Die Zeit läuft bewusst weiter. Was hinter dem Dialog den Fokus bekommen
      // wollte, bekommt ihn nach dem Schließen.
      let gewuenscht = null;
      const amDokument = (ev) => {
        if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); schliessen(); return; }
        if (ev.key === 'Tab') { ev.preventDefault(); ev.stopPropagation(); (document.activeElement === weiter ? verlassen : weiter).focus(); return; }
        if (!box.contains(ev.target)) { ev.preventDefault(); ev.stopPropagation(); }
      };
      const fokusFalle = (ev) => {
        if (box.contains(ev.target)) return;
        if (this.stage.contains(ev.target)) gewuenscht = ev.target;
        weiter.focus({ preventScroll: true });
      };
      const aus = () => {
        document.removeEventListener('keydown', amDokument, true);
        document.removeEventListener('focusin', fokusFalle, true);
        this._dialogAus = null;
      };
      const schliessen = () => {
        aus();
        box.remove();
        const aktuell = (el) => el && el.isConnected && this.stage.contains(el);
        const ziel = aktuell(gewuenscht) ? gewuenscht : aktuell(vorher) ? vorher : this.stage.querySelector('.thct-frage, .thct-titel');
        if (ziel) {
          ziel.focus({ preventScroll: true });
          // Fokus sichtbar machen, ohne die Seite springen zu lassen (QA final, Opus N14), und nicht unter der Kopfzeile
          // (Abnahme 2, Opus N1)
          inSicht(ziel);
        }
      };
      const weiter = h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: schliessen }, 'Weiter testen');
      const verlassen = h('button', { type: 'button', class: 'thct-link', onclick: () => { this.guardOff(); history.go(-2); } }, 'Test verlassen');
      const box = h('div', { class: 'thct-verlassen', role: 'alertdialog', 'aria-modal': 'true', 'aria-labelledby': 'thct-verlassen-t' },
        h('div', { class: 'thct-verlassen__box' },
          h('p', { id: 'thct-verlassen-t' }, h('b', { text: 'Test verlassen?' }), ' Deine Antworten bleiben gespeichert, aber die Zeit dieses Teils läuft weiter.'),
          h('div', { class: 'thct-zeile' }, weiter, verlassen)));
      // Escape, Tab-Falle und die Sperre für Buchstaben- und Zifferntasten der Aufgabe am Dokument (Erfassungsphase),
      // solange der Dialog offen ist
      document.addEventListener('keydown', amDokument, true);
      document.addEventListener('focusin', fokusFalle, true);
      this._dialogAus = aus;
      this.append(box);
      weiter.focus({ preventScroll: true });
    }

    resume(run) {
      this.run = run;
      this.stageHalten();
      if (this.run.phase === 'angaben') this.showAngaben();
      else if (this.run.item < 0) this.showBlockIntro();
      else this.showItem();
      // Fokus erst nach dem Klick setzen, sonst bleibt er am entfernten Startknopf hängen (Fable D14)
      setTimeout(() => {
        const f = this.stage.querySelector('.thct-frage, .thct-titel');
        if (f && !this.stage.contains(document.activeElement)) f.focus({ preventScroll: true });
      }, 0);
    }

    save() { store.set(this.kRun, this.run); }
    get blocks() { return this.run.session.blocks; }
    get block() { return this.blocks[this.run.block]; }
    get totalItems() { return this.blocks.reduce((n, b) => n + b.items.length, 0); }
    get itemNumber() { let n = 0; for (let i = 0; i < this.run.block; i++) n += this.blocks[i].items.length; return n + this.run.item + 1; }

    clearTimer() { if (this._timer) clearInterval(this._timer); this._timer = null; }

    renderMessage(text, busy) {
      this.clearTimer();
      this.stage.replaceChildren(h('div', { class: 'thct-meldung', role: 'status', 'aria-live': 'polite' }, busy ? h('span', { class: 'thct-spinner', 'aria-hidden': 'true' }) : null, h('p', { text })));
    }
    renderError(text, retry, neu) {
      this.clearTimer();
      this.stage.replaceChildren(h('div', { class: 'thct-meldung thct-meldung--fehler', role: 'alert' },
        h('p', { text }),
        h('div', { class: 'thct-zeile' },
          neu ? h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: neu }, 'Neu starten') : null,
          retry ? h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: retry }, 'Nochmal versuchen') : null)));
    }

    // Kopfzeile während des Tests
    head(timerEl) {
      const pct = Math.round(((this.itemNumber - 1) / this.totalItems) * 100);
      const bar = h('div', { class: 'thct-fortschritt', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': String(this.totalItems), 'aria-valuenow': String(this.itemNumber - 1), 'aria-label': 'Fortschritt' }, h('span', { style: 'width:' + pct + '%' }));
      return h('div', { class: 'thct-kopf' },
        h('div', { class: 'thct-kopf__zeile' },
          h('span', { class: 'thct-kopf__block' }, 'Teil ' + (this.run.block + 1) + ' von ' + this.blocks.length + ': ', h('b', { text: this.block.title })),
          timerEl),
        bar,
        h('p', { class: 'thct-kopf__nr' }, 'Aufgabe ', h('b', { text: String(this.itemNumber) }), ' von ' + this.totalItems));
    }

    showBlockIntro() {
      this.clearTimer();
      const b = this.block;
      const ex = b.example;
      const nodes = [
        h('p', { class: 'thct-eyebrow', text: 'Teil ' + (this.run.block + 1) + ' von ' + this.blocks.length }),
        h('h2', { class: 'thct-titel', tabindex: '-1', text: b.title }),
        h('p', { class: 'thct-text', text: b.instruction }),
        h('ul', { class: 'thct-fakten' },
          h('li', null, h('b', { text: String(b.items.length) }), ' Aufgaben'),
          h('li', null, 'höchstens ', h('b', { text: Math.round(b.timeLimitSec / 60) + ' Minuten' })),
          h('li', { text: 'Raten ist erlaubt' })),
      ];
      if (ex) {
        const exBox = h('div', { class: 'thct-beispiel' }, h('p', { class: 'thct-beispiel__titel', text: 'Beispiel (zählt nicht)' }));
        exBox.append(h('p', { class: 'thct-beispiel__frage', text: ex.item.prompt }));
        exBox.append(this.renderStimulus(ex.item, true));
        exBox.append(this.renderOptionsStatic(ex.item, ex.solution));
        exBox.append(h('p', { class: 'thct-beispiel__loesung' }, h('b', { text: 'Lösung: ' + this.solutionLabel(ex.item, ex.solution) + '. ' }), ex.explanation));
        nodes.push(exBox);
      }
      const go = h('button', { type: 'button', class: 'th-btn th-btn--primary thct-los', onclick: () => this.startBlock() }, 'Teil ' + (this.run.block + 1) + ' starten');
      nodes.push(h('div', { class: 'thct-aktionen' }, go));
      const wrap = h('div', { class: 'thct-blockstart' }, ...nodes);
      this.stage.replaceChildren(wrap);
      this.stageZeigen();
      wrap.querySelector('.thct-titel').focus({ preventScroll: true });
    }

    startBlock() {
      if (!this.run.blockStart[this.run.block]) this.run.blockStart[this.run.block] = Date.now();
      this.run.item = 0;
      this.save();
      this.guardOn();
      this.showItem();
    }

    remainingSec() {
      const st = this.run.blockStart[this.run.block] || Date.now();
      return this.block.timeLimitSec - (Date.now() - st) / 1000;
    }

    showItem() {
      this.clearTimer();
      if (this.remainingSec() <= 0) return this.blockTimeout();
      const item = this.block.items[this.run.item];
      const prev = this.run.answers[item.id];
      const offen = this.run.cur && this.run.cur.id === item.id ? this.run.cur : null;
      this.cur = { item, shownAt: offen ? offen.shownAt : Date.now(), value: offen && offen.value !== undefined ? offen.value : prev ? prev.answer : null, prevMs: prev ? prev.rtMs : 0 };
      this.run.cur = { id: item.id, shownAt: this.cur.shownAt, value: this.cur.value };
      this.save();
      this.guardOn();
      const timer = h('span', { class: 'thct-zeit', role: 'timer', 'aria-live': 'off' });
      const updateTimer = () => {
        const r = this.remainingSec();
        timer.textContent = 'Noch ' + mmss(r);
        timer.classList.toggle('is-knapp', r <= 60);
        timer.setAttribute('aria-label', 'Verbleibende Zeit in diesem Teil: ' + mmss(r));
        if (r <= 0) this.blockTimeout();
      };
      updateTimer();
      this._timer = setInterval(updateTimer, 1000);

      const frage = h('h2', { class: 'thct-frage', tabindex: '-1', text: item.prompt });
      const body = h('div', { class: 'thct-aufgabe thct-aufgabe--' + item.type });
      body.append(frage);
      const layout = h('div', { class: 'thct-aufgabe__raster' });
      layout.append(this.renderStimulus(item, false));
      const weiter = h('button', { type: 'button', class: 'th-btn th-btn--primary thct-weiter', disabled: true, onclick: () => this.next(false) }, this.isLastItem() ? 'Abschließen' : 'Weiter');
      const hinweis = h('p', { class: 'thct-auswahlhinweis', 'aria-live': 'polite' });
      this.cur.weiter = weiter;
      this.cur.hinweis = hinweis;
      layout.append(this.renderInput(item));
      body.append(layout);
      const skip = h('button', { type: 'button', class: 'thct-link', onclick: () => this.next(true) }, 'Überspringen');
      body.append(h('div', { class: 'thct-aktionen thct-aktionen--item' }, skip, h('div', { class: 'thct-aktionen__rechts' }, hinweis, weiter)));
      this.stage.replaceChildren(this.head(timer), body);
      this.updateWeiter();
      this.stageZeigen();
      frage.focus({ preventScroll: true });
      this.stage.onkeydown = (ev) => this.onKey(ev);
    }

    isLastItem() { return this.run.block === this.blocks.length - 1 && this.run.item === this.block.items.length - 1; }

    renderStimulus(item, small) {
      const box = h('div', { class: 'thct-reiz' + (small ? ' thct-reiz--klein' : '') });
      if (item.type === 'matrix') {
        const grid = h('div', { class: 'thct-matrix', role: 'img', 'aria-label': 'Muster aus drei mal drei Feldern, das Feld rechts unten fehlt' });
        item.cells.forEach((svg) => grid.append(h('div', { class: 'thct-zelle', html: svg })));
        grid.append(h('div', { class: 'thct-zelle thct-zelle--frage', 'aria-hidden': 'true' }, '?'));
        box.append(grid);
      } else if (item.type === 'series') {
        const row = h('ol', { class: 'thct-reihe', 'aria-label': 'Zahlenreihe' });
        item.terms.forEach((t) => row.append(h('li', { text: NF0.format(t) })));
        row.append(h('li', { class: 'thct-reihe__frage', 'aria-label': 'nächste Zahl gesucht' }, '?'));
        box.append(row);
      } else if (item.type === 'logic') {
        const ul = h('ul', { class: 'thct-aussagen' });
        item.premises.forEach((p, i) => ul.append(h('li', { class: i === 0 ? 'thct-aussagen__rahmen' : '', text: p })));
        box.append(ul);
      } else if (item.type === 'rotation') {
        box.append(h('figure', { class: 'thct-ziel' }, h('div', { class: 'thct-ziel__bild', html: item.targetSvg }), h('figcaption', { text: 'Ausgangsfigur' })));
      }
      return box;
    }

    // Antwortfelder (interaktiv)
    renderInput(item) {
      const f = item.format;
      if (f.kind === 'number') {
        const inp = h('input', { class: 'thct-zahl', type: 'text', inputmode: 'numeric', pattern: '[0-9]*', enterkeyhint: 'next', autocomplete: 'off', 'aria-label': 'Nächste Zahl eingeben' });
        if (this.cur.value !== null && this.cur.value !== undefined) inp.value = String(this.cur.value);
        inp.addEventListener('input', () => {
          inp.value = inp.value.replace(/[^0-9]/g, '').slice(0, f.maxLength || 5);
          this.cur.value = inp.value === '' ? null : Number(inp.value);
          this.updateWeiter();
        });
        inp.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' && !this.cur.weiter.disabled) { ev.preventDefault(); this.next(false); } });
        setTimeout(() => { if (geraet() === 'desktop') inp.focus({ preventScroll: true }); }, 50);
        const ok = h('button', { type: 'button', class: 'th-btn th-btn--primary thct-eingabe__ok', onclick: () => { if (!this.cur.weiter.disabled) this.next(false); } }, 'OK');
        this.cur.okBtn = ok;
        return h('div', { class: 'thct-eingabe' }, h('label', { class: 'thct-eingabe__label', text: 'Deine Antwort' }), h('div', { class: 'thct-eingabe__zeile' }, inp, ok));
      }
      const multi = f.kind === 'multi';
      const group = h('div', { class: 'thct-optionen thct-optionen--' + item.type + ' thct-optionen--n' + item.options.length, role: multi ? 'group' : 'radiogroup', 'aria-label': multi ? 'Zwei Figuren auswählen' : 'Antworten' });
      item.options.forEach((o, i) => {
        const sel = multi ? Array.isArray(this.cur.value) && this.cur.value.includes(o.id) : this.cur.value === o.id;
        const btn = h('button', {
          type: 'button',
          class: 'thct-option' + (o.svg ? ' thct-option--bild' : ' thct-option--text'),
          role: multi ? 'checkbox' : 'radio',
          'aria-checked': sel ? 'true' : 'false',
          tabindex: multi ? '0' : (sel || (i === 0 && !this.cur.value) ? '0' : '-1'),
          'data-id': o.id,
          'aria-label': (multi ? 'Figur ' : 'Antwort ') + LETTERS[i] + (o.text ? ': ' + o.text : ''),
        },
        h('span', { class: 'thct-option__buchstabe', 'aria-hidden': 'true', text: LETTERS[i] }),
        o.svg ? h('span', { class: 'thct-option__bild', html: o.svg }) : h('span', { class: 'thct-option__text', text: o.text }));
        btn.addEventListener('click', () => this.choose(o.id, multi, f.pick));
        group.append(btn);
      });
      if (multi) {
        // Enter bestätigt, sobald zwei Figuren gewählt sind, und schaltet die fokussierte Figur nicht um; umgeschaltet
        // wird mit Leertaste, Klick oder Buchstabe (QA-Runde 3, Opus N-1: Enter hob die Wahl wieder auf)
        group.addEventListener('keydown', (ev) => {
          if (ev.key !== 'Enter') return;
          ev.preventDefault();
          if (!this.cur.weiter.disabled) this.next(false);
        });
      } else {
        group.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') {
            // Enter bestätigt die gewählte Antwort, statt die fokussierte Option erneut zu wählen (Opus N-M3)
            ev.preventDefault();
            if (!this.cur.weiter.disabled) this.next(false);
            return;
          }
          const btns = [...group.querySelectorAll('.thct-option')];
          const idx = btns.indexOf(document.activeElement);
          if (idx < 0) return;
          // Spalten wie im Layout: Matrix-Antworten unter 600 px in 3 Spalten
          const cols = item.type === 'matrix' ? (window.innerWidth < 600 ? 3 : 4) : 1;
          let n = null;
          if (ev.key === 'ArrowRight') n = idx + 1;
          else if (ev.key === 'ArrowLeft') n = idx - 1;
          else if (ev.key === 'ArrowDown') n = idx + cols;
          else if (ev.key === 'ArrowUp') n = idx - cols;
          if (n === null) return;
          ev.preventDefault();
          n = (n + btns.length) % btns.length;
          btns.forEach((b) => b.setAttribute('tabindex', '-1'));
          btns[n].setAttribute('tabindex', '0');
          btns[n].focus();
          this.choose(btns[n].dataset.id, false);
        });
      }
      this.cur.group = group;
      return group;
    }

    choose(id, multi, pick) {
      if (multi) {
        let v = Array.isArray(this.cur.value) ? this.cur.value.slice() : [];
        if (v.includes(id)) v = v.filter((x) => x !== id);
        else { v.push(id); if (v.length > pick) v.shift(); }
        this.cur.value = v;
      } else {
        this.cur.value = id;
      }
      this.cur.group.querySelectorAll('.thct-option').forEach((b) => {
        const on = multi ? this.cur.value.includes(b.dataset.id) : b.dataset.id === id;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        if (!multi) b.setAttribute('tabindex', on ? '0' : '-1');
      });
      this.updateWeiter();
    }

    updateWeiter() {
      const f = this.cur.item.format;
      let ok = this.cur.value !== null && this.cur.value !== undefined && this.cur.value !== '';
      if (f.kind === 'multi') {
        const n = Array.isArray(this.cur.value) ? this.cur.value.length : 0;
        ok = n === f.pick;
        this.cur.hinweis.textContent = n === f.pick ? '' : n + ' von ' + f.pick + ' gewählt';
      }
      this.cur.weiter.disabled = !ok;
      if (this.cur.okBtn) this.cur.okBtn.disabled = !ok;
      if (this.run && this.run.cur && this.run.cur.id === this.cur.item.id) { this.run.cur.value = this.cur.value; this.save(); }
    }

    onKey(ev) {
      if (!this.cur || ev.target.tagName === 'INPUT' || ev.altKey || ev.ctrlKey || ev.metaKey) return;
      const item = this.cur.item;
      if (item.format.kind === 'number') return;
      const k = ev.key.toUpperCase();
      let idx = LETTERS.indexOf(k);
      if (idx < 0 && /^[1-8]$/.test(ev.key)) idx = Number(ev.key) - 1;
      if (idx >= 0 && idx < item.options.length) {
        ev.preventDefault();
        this.choose(item.options[idx].id, item.format.kind === 'multi', item.format.pick);
        // Fokus auf die gewählte Option, damit Enter danach bestätigt und nicht eine andere Option wählt
        const b = this.cur.group && [...this.cur.group.querySelectorAll('.thct-option')].find((x) => x.dataset.id === item.options[idx].id);
        if (b) b.focus({ preventScroll: true });
      } else if (ev.key === 'Enter' && !this.cur.weiter.disabled && !(ev.target.closest && ev.target.closest('button, a, input, select, textarea'))) {
        ev.preventDefault();
        this.next(false);
      }
    }

    next(skip) {
      const it = this.cur.item;
      const rt = this.cur.prevMs + (Date.now() - this.cur.shownAt);
      this.run.answers[it.id] = { id: it.id, answer: skip ? null : this.cur.value, rtMs: rt };
      this.advance();
    }

    advance() {
      if (this.run.item < this.block.items.length - 1) {
        this.run.item++;
        this.save();
        return this.showItem();
      }
      return this.endBlock(false);
    }

    blockTimeout() {
      this.clearTimer();
      // bereits gewählte, aber noch nicht bestätigte Antwort der aktuellen Aufgabe gilt (fair bei Zeitablauf)
      const cur = this.cur;
      if (cur && cur.item.block === this.block.key && !this.run.answers[cur.item.id]) {
        const v = cur.value;
        const pick = cur.item.format.pick || 0;
        const ok = v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length < pick);
        if (ok) this.run.answers[cur.item.id] = { id: cur.item.id, answer: v, rtMs: cur.prevMs + (Date.now() - cur.shownAt) };
      }
      // unbeantwortete Aufgaben dieses Teils als „Zeit abgelaufen“ markieren
      for (const it of this.block.items) if (!this.run.answers[it.id]) this.run.answers[it.id] = { id: it.id, answer: null, rtMs: 0, timedOut: true };
      this.cur = null;
      this.endBlock(true);
    }

    endBlock(timedOut) {
      this.clearTimer();
      const last = this.run.block === this.blocks.length - 1;
      const title = this.block.title;
      if (!last) { this.run.block++; this.run.item = -1; }
      else { this.run.item = this.block.items.length; this.run.phase = 'angaben'; }
      this.save();
      if (timedOut) {
        const msg = h('div', { class: 'thct-meldung', role: 'status' },
          h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Die Zeit für „' + title + '“ ist abgelaufen' }),
          h('p', { text: (last ? 'Offene Aufgaben zählen als nicht gelöst. Es fehlen nur noch ein paar freiwillige Angaben.' : 'Offene Aufgaben dieses Teils zählen als nicht gelöst. Weiter geht es mit dem nächsten Teil.') + ' Hattest du bei der letzten Aufgabe schon eine Antwort gewählt, zählt sie.' }),
          h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => (last ? this.showAngaben() : this.showBlockIntro()) }, 'Weiter'));
        this.stage.replaceChildren(msg);
        msg.querySelector('h2').focus({ preventScroll: true });
        return;
      }
      if (last) this.showAngaben();
      else this.showBlockIntro();
    }

    // ── Angaben („Fast geschafft“) ───────────────────────────────────────
    showAngaben() {
      this.clearTimer();
      this.guardOff(); // keine Zeit mehr im Spiel, Verlassen ist hier unkritisch
      const a = (this.run.angaben = this.run.angaben || {});
      const chips = (name, label, opts) => {
        const grp = h('div', { class: 'thct-chips', role: 'radiogroup', 'aria-label': label });
        opts.forEach(([v, t]) => {
          const b = h('button', { type: 'button', class: 'thct-chip', role: 'radio', 'aria-checked': a[name] === v ? 'true' : 'false', onclick: () => {
            a[name] = a[name] === v ? null : v;
            grp.querySelectorAll('.thct-chip').forEach((x) => x.setAttribute('aria-checked', x.dataset.v === a[name] ? 'true' : 'false'));
            this.save();
          } }, t);
          b.dataset.v = v;
          grp.append(b);
        });
        return h('fieldset', { class: 'thct-feld' }, h('legend', { text: label }), grp);
      };
      const select = (name, label, opts) => {
        const sel = h('select', { class: 'thct-select', id: 'thct-' + name, onchange: (ev) => { a[name] = ev.target.value || null; this.save(); } },
          h('option', { value: '', text: 'Bitte wählen (freiwillig)' }), ...opts.map(([v, t]) => h('option', { value: v, text: t, selected: a[name] === v })));
        return h('div', { class: 'thct-feld' }, h('label', { for: 'thct-' + name, text: label }), sel);
      };
      const alter = h('input', { class: 'thct-input thct-input--kurz', id: 'thct-alter', type: 'text', inputmode: 'numeric', pattern: '[0-9]*', maxlength: '2', autocomplete: 'off', value: a.alter || '', oninput: (ev) => { ev.target.value = ev.target.value.replace(/[^0-9]/g, ''); a.alter = ev.target.value || null; this.save(); } });

      // Wohnort: PLZ (optional) mit Sofortprüfung, Ausland. Kein sichtbarer IP-Vorschlag mehr (Bestätigungs-Bias).
      const plzStatus = h('p', { class: 'thct-plz__status', 'aria-live': 'polite' });
      const plzWahl = h('div', { class: 'thct-plz__wahl', hidden: true });
      const plz = h('input', { class: 'thct-input thct-input--kurz', id: 'thct-plz', type: 'text', inputmode: 'numeric', pattern: '[0-9]*', maxlength: '5', autocomplete: 'postal-code', value: a.plz || '', 'aria-describedby': 'thct-plz-hilfe' });
      const ausland = h('input', { type: 'checkbox', id: 'thct-ausland', checked: !!a.ausland });
      const lookup = async () => {
        const v = plz.value;
        a.plz = v || null;
        a.plzLand = null;
        plzWahl.hidden = true;
        if (v.length < 5) { plzStatus.textContent = ''; plzStatus.className = 'thct-plz__status'; this.save(); return; }
        plzStatus.textContent = 'Prüfe …';
        try {
          const r = await api(this.api, '/api/mobile/geo/plz', { method: 'POST', body: JSON.stringify({ plz: v }) });
          if (plz.value !== v) return;
          if (r.status !== 'ok') { plzStatus.textContent = 'Diese PLZ kennen wir nicht. Bitte prüfen (oder leer lassen).'; plzStatus.className = 'thct-plz__status is-fehler'; return; }
          if (!r.mehrdeutig) {
            plzStatus.textContent = r.kandidaten[0].anzeige + ' ✓';
            plzStatus.className = 'thct-plz__status is-ok';
          } else {
            plzStatus.textContent = 'Diese PLZ liegt in mehreren Bundesländern. Wo wohnst du?';
            plzStatus.className = 'thct-plz__status';
            plzWahl.replaceChildren(...r.kandidaten.map((k) => {
              const id = 'thct-pl-' + k.land;
              const rb = h('input', { type: 'radio', name: 'thct-plzland', id, value: k.land, onchange: () => { a.plzLand = k.land; this.save(); } });
              return h('label', { class: 'thct-radio', for: id }, rb, ' ' + k.ort + ', ' + k.landName);
            }));
            plzWahl.hidden = false;
          }
          this.save();
        } catch (e) {
          plzStatus.textContent = 'Die PLZ konnte gerade nicht geprüft werden. Du kannst trotzdem weitermachen.';
          plzStatus.className = 'thct-plz__status';
        }
      };
      plz.addEventListener('input', () => { plz.value = plz.value.replace(/[^0-9]/g, '').slice(0, 5); if (ausland.checked && plz.value) { ausland.checked = false; a.ausland = false; } lookup(); });
      ausland.addEventListener('change', () => {
        a.ausland = ausland.checked;
        if (ausland.checked) { plz.value = ''; a.plz = null; plzStatus.textContent = ''; plzWahl.hidden = true; }
        this.save();
      });
      const wohnort = h('fieldset', { class: 'thct-feld thct-plz' },
        h('legend', { text: 'Postleitzahl' }),
        h('div', { class: 'thct-plz__zeile' }, plz, h('label', { class: 'thct-check', for: 'thct-ausland' }, ausland, ' Ich wohne nicht in Deutschland')),
        plzStatus, plzWahl,
        h('p', { class: 'thct-hilfe', id: 'thct-plz-hilfe', text: 'Aus der PLZ leiten wir Ort, Landkreis, Bundesland und Ortsgröße ab; die PLZ selbst und die Leitregion speichern wir nicht, das Alter nur als Altersgruppe. In der Statistik erscheint jeder Ort ab der ersten Teilnahme, ohne Namen; bist du die erste Person aus deinem Ort, ist der dort gezeigte Wert dein eigener. Mit PLZ speichern wir außerdem nur, ob das grob aus der IP-Adresse geschätzte Bundesland dazu passt (ja oder nein). Ohne PLZ speichern wir dieses geschätzte Bundesland (nur für interne Qualitätsprüfungen, nicht für die Statistik). Die IP-Adresse selbst wird nicht gespeichert.' }));

      const optOut = h('input', { type: 'checkbox', id: 'thct-optout', checked: !!a.statistikOptOut, onchange: (ev) => { a.statistikOptOut = ev.target.checked; this.save(); } });
      // Kein <form>: Mit Marketing-Einwilligung meldet Googles Formularerkennung sonst form_start/form_submit dieses
      // Formulars (QA-Runde 3, Opus N-10). Enter in einem Textfeld schickt weiter ab wie vorher.
      const form = h('div', { class: 'thct-angaben', role: 'group', 'aria-labelledby': 'thct-angaben-t', onkeydown: (ev) => {
        if (ev.key === 'Enter' && ev.target.tagName === 'INPUT' && ev.target.type === 'text') { ev.preventDefault(); this.submit(false); }
      } },
        h('p', { class: 'thct-eyebrow', text: 'Fast geschafft' }),
        h('h2', { class: 'thct-titel', id: 'thct-angaben-t', tabindex: '-1', text: 'Noch ein paar Angaben für die Statistik' }),
        h('p', { class: 'thct-text', text: 'Alles freiwillig. Deine Angaben helfen, die Ergebnisse besser einzuordnen und die Normen zu verbessern. Dein Ergebnis siehst du in jedem Fall.' }),
        h('div', { class: 'thct-angaben__raster' },
          h('div', { class: 'thct-feld' }, h('label', { for: 'thct-alter', text: 'Alter in Jahren' }), alter),
          chips('geschlecht', 'Geschlecht', [['w', 'weiblich'], ['m', 'männlich'], ['d', 'divers'], ['k', 'keine Angabe']]),
          wohnort,
          select('bildung', 'Höchster Abschluss', [['schule', 'Gehe noch zur Schule'], ['ohne', 'Ohne Schulabschluss'], ['haupt', 'Hauptschulabschluss'], ['real', 'Mittlere Reife / Realschulabschluss'], ['abitur', 'Abitur / Fachabitur'], ['ausbildung', 'Abgeschlossene Berufsausbildung'], ['bachelor', 'Bachelor / FH-Diplom'], ['master', 'Master / Diplom / Staatsexamen'], ['promotion', 'Promotion'], ['k', 'keine Angabe']]),
          chips('muttersprache', 'Muttersprache', [['de', 'Deutsch'], ['zwei', 'Deutsch und eine weitere'], ['andere', 'Eine andere Sprache']]),
          chips('vorerfahrung', 'Schon einmal einen IQ-Test gemacht?', [['nie', 'Noch nie'], ['andere', 'Ja, andere'], ['dieser', 'Ja, genau diesen']]),
          select('anlass', 'Wofür machst du den Test?', [['neugier', 'Aus Neugier'], ['einstellungstest', 'Vorbereitung auf einen Einstellungs- oder Eignungstest'], ['schule_studium', 'Für Schule oder Studium'], ['mensa', 'Vorbereitung auf den Mensa-Test'], ['sonstiges', 'Etwas anderes']])),
        h('label', { class: 'thct-check thct-check--optout', for: 'thct-optout' }, optOut, ' Meinen Durchlauf nicht in die Statistik aufnehmen'),
        h('p', { class: 'thct-hilfe' }, 'Dein Durchlauf fließt ohne Namen, E-Mail und IP-Adresse in die Statistik ein; das kannst du mit dem Haken abwählen. Dann speichern wir nur Antworten, Zeiten und Ergebnis, aber keine Angaben und keine Region. Dasselbe gilt für „Ohne Angaben zum Ergebnis“. ', h('a', { href: '/policies/privacy-policy', text: 'Datenschutz' }), '. Region ohne PLZ: IP Geolocation by ', h('a', { href: 'https://db-ip.com', rel: 'noopener', text: 'DB-IP' }), '.'),
        h('div', { class: 'thct-aktionen' },
          h('button', { type: 'button', class: 'thct-link', onclick: () => this.submit(true) }, 'Ohne Angaben zum Ergebnis'),
          h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.submit(false) }, 'Ergebnis anzeigen')));
      this.stage.replaceChildren(form);
      this.stageZeigen();
      form.querySelector('.thct-titel').focus({ preventScroll: true });
      if (a.plz && a.plz.length === 5) lookup();
    }

    async submit(ohneAngaben) {
      const a = ohneAngaben ? { statistikOptOut: !!(this.run.angaben || {}).statistikOptOut, ohneAngaben: true } : Object.assign({}, this.run.angaben || {});
      // „Ohne Angaben“: kein Alter senden, aber den Schutz „unter 18“, damit auch die Antwort keine Zahl enthält (QA final, Opus N18)
      const angabeAlter = this.run.angaben && this.run.angaben.alter ? Number(this.run.angaben.alter) : null;
      const unter18 = ohneAngaben && angabeAlter !== null && angabeAlter < 18;
      const hist = store.get(this.kHist) || { anzahl: 0 };
      const answers = [];
      for (const b of this.blocks) for (const it of b.items) {
        const x = this.run.answers[it.id];
        answers.push(x ? x : { id: it.id, answer: null, rtMs: 0, timedOut: true });
      }
      this.stageHalten(2);
      this.renderMessage('Dein Ergebnis wird berechnet …', true);
      try {
        const res = await api(this.api, '/api/mobile/iq-test/submit', {
          method: 'POST',
          body: JSON.stringify({
            token: this.run.session.token,
            answers,
            angaben: { alter: a.alter ? Number(a.alter) : null, geschlecht: a.geschlecht, bildung: a.bildung, muttersprache: a.muttersprache, vorerfahrung: a.vorerfahrung, anlass: a.anlass, plz: a.plz, plzLand: a.plzLand, ausland: !!a.ausland, statistikOptOut: !!a.statistikOptOut, ohneAngaben: !!a.ohneAngaben, unter18: unter18 || undefined },
            geraet: geraet(),
            fruehereDurchlaeufe: hist.anzahl || 0,
            seite: location.pathname,
          }),
        });
        this.run.done = true;
        this.guardOff();
        store.del(this.kRun);
        // unter 18 keine Zahl, auch nicht im Hinweis beim nächsten Besuch
        if (res.iq !== null && res.iq !== undefined && !istJung(res, this.run)) store.set(this.kHist, { anzahl: (hist.anzahl || 0) + 1, letztes: { iq: res.iq, ciLow: res.ciLow, ciHigh: res.ciHigh, am: Date.now() } });
        else store.set(this.kHist, { anzahl: (hist.anzahl || 0) + 1 });
        this.showResult(res);
        document.dispatchEvent(new CustomEvent('thct:ergebnis', { detail: { instrument: this.instrument } }));
      } catch (e) {
        if (e.status === 400) {
          // Sitzung abgelaufen, ungültig oder alte Fassung: „Nochmal versuchen“ führte hier in eine Sackgasse (Fable D3)
          this.renderError((e.message || 'Die Testsitzung ist abgelaufen oder ungültig.') + ' Diese Antworten lassen sich leider nicht mehr auswerten.', null, () => { this.guardOff(); store.del(this.kRun); this.neuStart(); });
          return;
        }
        this.renderError((e.message || 'Die Auswertung ist fehlgeschlagen.') + ' Deine Antworten sind gespeichert.', () => this.submit(ohneAngaben));
      }
    }

    // ── Ergebnis ──────────────────────────────────────────────────────────
    showResult(r) {
      this.clearTimer();
      this.guardOff();
      const wrap = h('div', { class: 'thct-ergebnis' });
      const klasse = (iq) => (iq >= 130 ? 'weit überdurchschnittlich' : iq >= 115 ? 'überdurchschnittlich' : iq >= 85 ? 'durchschnittlich' : iq >= 70 ? 'unterdurchschnittlich' : 'deutlich unterdurchschnittlich');
      const jung = istJung(r, this.run);
      this.jung = jung;
      const mb = r.messbereich;
      const nichtAuswertbar = r.auswertbar === false;
      if (nichtAuswertbar) {
        // Durchklicken/Raten: keine IQ-Zahl, sondern ehrlich sagen, warum (Abnahme-QA)
        wrap.append(
          h('p', { class: 'thct-eyebrow', text: 'Dein Ergebnis' }),
          h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Dieser Durchlauf ist nicht sinnvoll auswertbar' }),
          h('div', { class: 'thct-hinweis thct-hinweis--gross', role: 'note' },
            h('p', { text: 'Wir zeigen dir bewusst keine IQ-Schätzung, weil sie bei diesem Durchlauf nichts über dein Denken aussagen würde:' }),
            h('ul', null, ...(r.nichtAuswertbarGrund || []).map((t) => h('li', { text: t }))),
            h('p', { text: r.raw + ' von ' + r.max + ' Aufgaben gelöst. Nimm dir für einen neuen Versuch etwa 20 Minuten Ruhe, lies jede Aufgabe genau und rate erst, wenn du nicht weiterkommst.' })),
          h('div', { class: 'thct-aktionen' }, h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.neuStart() }, 'In Ruhe neu starten')));
      } else if (jung) {
        // Unter 18 keine IQ-Zahl (QA-Runde 3, Opus M-7): Für diesen Test gibt es keine Jugendnormen, eine Zahl nach der
        // Erwachsenen-Norm wäre verzerrt und sähe trotzdem wie ein Befund aus. Nur gelöste Aufgaben, je Teil ohne
        // Erwartungswert; gilt auch, wenn der Server das Alter nicht kennt („Ohne Angaben“) und eine Zahl schickt.
        const anteil = r.max ? r.raw / r.max : 0;
        const menge = anteil >= 0.8 ? 'fast alle' : anteil >= 0.6 ? 'die meisten' : anteil >= 0.4 ? 'ungefähr die Hälfte der' : anteil >= 0.2 ? 'einen Teil der' : 'nur wenige';
        wrap.append(
          h('p', { class: 'thct-eyebrow', text: 'Dein Ergebnis' }),
          h('h2', { class: 'thct-titel', tabindex: '-1', text: r.raw + ' von ' + r.max + ' Aufgaben gelöst' }));
        if (r.hinweise && r.hinweise.length) wrap.append(h('div', { class: 'thct-hinweis', role: 'note' }, ...r.hinweise.map((t) => h('p', { text: t }))));
        wrap.append(
          h('div', { class: 'thct-hinweis thct-hinweis--gross', role: 'note' },
            h('p', null, h('b', { text: 'Unter 18 zeigen wir keine IQ-Zahl. ' }), 'Für diesen Test gibt es noch keine Vergleichswerte für Jugendliche. Schlussfolgerndes Denken entwickelt sich bis ins junge Erwachsenenalter weiter; eine Zahl nach den Werten Erwachsener wäre für dich deshalb verzerrt.'),
            h('p', { text: 'Du hast ' + menge + ' Aufgaben gelöst. Wie viele es in jedem Teil waren, steht unten; die Lösungen erklären jede Aufgabe, auch die, die nicht geklappt haben.' })),
          h('section', { class: 'thct-profil', 'aria-labelledby': 'thct-profil-t' },
            h('h3', { id: 'thct-profil-t', text: 'Gelöst je Teil' }),
            h('p', { class: 'thct-klein', text: 'Je Teil gibt es nur 4 bis 12 Aufgaben, und die Teile sind verschieden schwer. Ein Vergleich der Teile untereinander sagt deshalb wenig.' }),
            h('ul', { class: 'thct-profil__liste' }, ...(r.subscales || []).map((sc) => h('li', { class: 'thct-teil' },
              h('div', { class: 'thct-profil__kopf' }, h('span', { text: sc.label }), h('span', { class: 'thct-klein', text: sc.raw + ' von ' + sc.max + ' gelöst' })),
              h('div', { class: 'thct-teil__spur', 'aria-hidden': 'true' }, h('span', { class: 'thct-teil__wert', style: 'width:' + Math.round((sc.max ? sc.raw / sc.max : 0) * 100) + '%' })))))));
      } else if (r.reportsIq && r.iq !== null) {
        const punkt = klasse(r.iq);
        const spanne = klasse(r.ciLow) === klasse(r.ciHigh) ? null : 'der Bereich reicht von ' + klasse(r.ciLow) + ' bis ' + klasse(r.ciHigh);
        const einordnung = h('p', null, 'Einordnung: wahrscheinlich ', h('b', { text: punkt }), spanne ? ' (' + spanne + ').' : '.');
        wrap.append(
          h('p', { class: 'thct-eyebrow', text: 'Dein Ergebnis' }),
          h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Deine vorläufige IQ-Schätzung' }));
        if (r.hinweise && r.hinweise.length) wrap.append(h('div', { class: 'thct-hinweis', role: 'note' }, ...r.hinweise.map((t) => h('p', { text: t }))));
        wrap.append(
          h('div', { class: 'thct-held' },
            h('div', { class: 'thct-held__zahl' }, h('span', { class: 'thct-held__einheit', text: 'vorläufige Schätzung' }), h('span', { class: 'thct-held__iq', text: String(r.iq) }), h('span', { class: 'thct-held__band', text: 'Bereich ' + r.ciLow + ' bis ' + r.ciHigh })),
            h('div', { class: 'thct-held__text' },
              h('p', null, h('b', { text: '95-%-Bereich: ' + r.ciLow + ' bis ' + r.ciHigh + '. ' }), 'In diesem Bereich liegt dein Wert mit hoher Wahrscheinlichkeit. Kein Test misst auf den Punkt genau.'),
              einordnung,
              h('p', { text: 'Geschätzter Prozentrang nach vorläufiger Norm: etwa ' + NF0.format(r.percentile) + '.' }),
              mb ? h('p', { class: 'thct-klein', text: 'Messbereich: Mit ' + r.max + ' Aufgaben kann dieser Test Werte etwa zwischen ' + mb.min + ' und ' + mb.max + ' unterscheiden.' + (r.iq >= mb.max - 3 ? ' Du liegst an der oberen Grenze; höhere Werte kann er nicht auflösen.' : r.iq <= mb.min + 3 ? ' Du liegst an der unteren Grenze.' : '') }) : null,
              h('p', { class: 'thct-klein', text: r.raw + ' von ' + r.max + ' Aufgaben gelöst. Norm: ' + (r.norm && r.norm.status === 'vorlaeufig' ? 'vorläufig, aus dem Aufbau der Aufgaben geschätzt; wir rechnen sie mit echten Daten nach.' : 'kalibriert.') }))),
          this.bellCurve(r.iq, r.ciLow, r.ciHigh));
      } else {
        wrap.append(h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Dein Ergebnis' }), h('p', { class: 'thct-held__text', text: r.raw + ' von ' + r.max + ' Aufgaben gelöst.' }));
      }
      // Ergebnis als PDF per E-Mail (Philipp 20.10.2026), nicht für unter 18 und nicht ohne Auswertung
      if (!nichtAuswertbar && !jung && this.run && this.run.session && this.run.session.token) wrap.append(this.pdfBox());
      if (!nichtAuswertbar && !jung && r.subscales && r.subscales.length) wrap.append(this.profile(r.subscales));
      if (!nichtAuswertbar && !jung) wrap.append(this.community(r));
      // ohne Ergebnis keine Erläuterung des Ergebnisses und keine Produktkarten (Fable D13, Opus N-N7); unter 18 steht
      // die Erläuterung schon im Hinweis oben
      if (!nichtAuswertbar && !jung) wrap.append(h('div', { class: 'thct-einordnung' },
        h('h3', { text: 'So ist das Ergebnis zu verstehen' }),
        h('ul', null,
          h('li', { text: 'Das ist eine Schätzung aus einem Online-Test, kein klinischer oder diagnostischer Intelligenztest. Für Entscheidungen über Schule, Beruf oder Förderung gehört ein Test unter Aufsicht einer Fachperson.' }),
          h('li', { text: 'Die Norm ist vorläufig: Sie wurde aus dem Aufbau der Aufgaben und Lösungsquoten aus der Fachliteratur geschätzt. Sobald genug Teilnahmen vorliegen, rechnen wir sie mit echten Daten nach.' }),
          h('li', { text: 'Verglichen wird mit Erwachsenen allgemein, nicht nur mit deiner Altersgruppe. Schlussfolgerndes Denken erreicht im jungen Erwachsenenalter seinen Höhepunkt und nimmt danach im Mittel langsam ab.' }),
          h('li', { text: 'Tagesform, Müdigkeit, Ablenkung und Bildschirmgröße beeinflussen das Ergebnis. Wer den Test wiederholt, schneidet durch Übung meist etwas besser ab.' }))));
      if (!nichtAuswertbar) wrap.append(this.recommendations(r));
      // „nicht auswertbar“: nur der eine Neustart-Knopf oben (Opus N-7)
      if (!nichtAuswertbar) wrap.append(this.shareRow(r));
      wrap.append(this.review(r));
      this.stage.replaceChildren(wrap);
      scrollIntoViewTop(this);
      wrap.querySelector('.thct-titel').focus({ preventScroll: true });
      // Die Mindesthöhe (zwei Fensterhöhen, gegen Layoutsprünge beim Nachladen) wird nach dem Ergebnis nicht mehr
      // gebraucht; sonst blieb unter einem kurzen Ergebnis viel leerer Raum (Philipp 20.10.2026)
      requestAnimationFrame(() => { this.stage.style.minHeight = ''; });
    }

    neuStart() {
      store.del(this.kRun);
      this.stage.innerHTML = this.introHtml;
      this.bindIntro();
      scrollIntoViewTop(this);
      // Fokus auf den Startknopf statt auf BODY bzw. den Skip-Link (QA-Runde 3, Opus N-6, Fable N16)
      const start = this.stage.querySelector('[data-thct-start]');
      if (start) start.focus({ preventScroll: true });
    }

    bellCurve(iq, lo, hi) {
      const W = Math.round(Math.min(760, Math.max(300, this.stage.clientWidth - 8))), H = 210, padL = 16, padR = 16, top = 34, base = 170;
      const x = (v) => padL + ((v - 55) / 90) * (W - padL - padR);
      const maxD = normalPdf(100, 100, 15);
      const y = (v) => base - (normalPdf(v, 100, 15) / maxD) * (base - top);
      let d = '';
      for (let v = 55; v <= 145; v += 1) d += (v === 55 ? 'M' : 'L') + x(v).toFixed(1) + ',' + y(v).toFixed(1);
      let band = 'M' + x(lo).toFixed(1) + ',' + base;
      for (let v = lo; v <= hi; v += 1) band += 'L' + x(v).toFixed(1) + ',' + y(v).toFixed(1);
      band += 'L' + x(hi).toFixed(1) + ',' + base + 'Z';
      const lx = Math.min(W - 44, Math.max(44, x(iq)));
      const svg = s('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'thct-glocke', role: 'img', 'aria-label': 'Glockenkurve der IQ-Verteilung (Mittelwert 100, Standardabweichung 15). Deine Schätzung ' + iq + ', Bereich ' + lo + ' bis ' + hi + '.' },
        s('path', { d: d + 'L' + x(145) + ',' + base + 'L' + x(55) + ',' + base + 'Z', class: 'thct-glocke__flaeche' }),
        s('path', { d: band, class: 'thct-glocke__band' }),
        s('path', { d, class: 'thct-glocke__linie' }),
        s('line', { x1: x(55), x2: x(145), y1: base, y2: base, class: 'thct-achse' }),
        ...[70, 85, 100, 115, 130].map((t) => s('g', null, s('line', { x1: x(t), x2: x(t), y1: base, y2: base + 5, class: 'thct-achse' }), s('text', { x: x(t), y: base + 20, 'text-anchor': 'middle', class: 'thct-achsentext' }, String(t)))),
        // Beschriftung oberhalb der Linie, Linie beginnt darunter (überschneidet die Beschriftung nicht)
        s('line', { x1: x(iq), x2: x(iq), y1: top - 8, y2: base, class: 'thct-glocke__marke' }),
        s('circle', { cx: x(iq), cy: y(iq), r: 6, class: 'thct-glocke__punkt' }),
        s('text', { x: lx, y: top - 14, 'text-anchor': 'middle', class: 'thct-glocke__label' }, 'Du: ' + iq));
      return h('figure', { class: 'thct-glocke-wrap' }, svg, h('figcaption', { text: 'Fläche unter der Kurve: Verteilung der IQ-Werte in der Bevölkerung. Hellblau: dein 95-%-Bereich.' }));
    }

    // Teilbereiche relativ zum Gesamtwert (keine eigenen Skalenwerte: je Teil zu wenige Aufgaben, Scheinstärken)
    profile(subs) {
      const relativ = subs.every((sc) => sc.einordnung);
      const txt = { staerke: 'relative Stärke', schwaeche: 'relative Schwäche', im_rahmen: 'im Rahmen deines Gesamtergebnisses' };
      const rows = subs.map((sc) => {
        const anteil = sc.max ? sc.raw / sc.max : 0;
        const erw = relativ && sc.max ? Math.max(0, Math.min(1, sc.erwartet / sc.max)) : null;
        const bar = h('div', { class: 'thct-teil__spur', 'aria-hidden': 'true' },
          h('span', { class: 'thct-teil__wert', style: 'width:' + Math.round(anteil * 100) + '%' }),
          erw !== null ? h('span', { class: 'thct-teil__erwartet', style: 'left:' + Math.round(erw * 100) + '%' }) : null);
        return h('li', { class: 'thct-teil' + (relativ ? ' is-' + sc.einordnung : '') },
          h('div', { class: 'thct-profil__kopf' }, h('span', { text: sc.label }), h('span', { class: 'thct-klein', text: sc.raw + ' von ' + sc.max + ' gelöst' })),
          bar,
          relativ ? h('p', { class: 'thct-klein thct-teil__text' }, h('b', { text: txt[sc.einordnung] }), ' · bei deinem Gesamtwert erwartet: etwa ' + NF1.format(sc.erwartet).replace(',0', '') + ' von ' + sc.max) : null);
      });
      return h('section', { class: 'thct-profil', 'aria-labelledby': 'thct-profil-t' },
        h('h3', { id: 'thct-profil-t', text: 'Deine Teilbereiche' }),
        h('p', { class: 'thct-klein', text: 'Balken: gelöster Anteil. Strich: wie viele Aufgaben bei deinem Gesamtwert zu erwarten wären. Je Teil gibt es nur 4 bis 12 Aufgaben; als Stärke oder Schwäche zählt nur eine Abweichung, die auch bei so wenigen Aufgaben kaum Zufall sein kann (strenger Test über alle vier Teile).' }),
        h('ul', { class: 'thct-profil__liste' }, ...rows));
    }

    community(r) {
      const c = r.community;
      const box = h('section', { class: 'thct-vergleich', 'aria-labelledby': 'thct-vgl-t' }, h('h3', { id: 'thct-vgl-t', text: 'Du im Vergleich' }));
      if (!c) return box;
      if (Array.isArray(c.gruppen)) {
        // Vergleich mit Gleichen (Philipp 20.10.2026): je Gruppe Mittelwert der anderen und „besser als k von n“
        const ARTEN = { alter: 'deiner Altersgruppe', geschlecht: 'deinem Geschlecht', bundesland: 'deinem Bundesland', ort: 'deinem Ort' };
        const arten = c.gruppen.map((g) => ARTEN[g.art]).filter(Boolean);
        const liste2 = arten.length > 1 ? arten.slice(0, -1).join(', ') + ' und ' + arten[arten.length - 1] : arten[0];
        box.append(h('p', { class: 'thct-klein', text: 'Dein Wert (roter Strich) neben dem Mittelwert der anderen Teilnehmenden (blauer Punkt)' + (liste2 ? ', insgesamt und in ' + liste2 : '') + '. Ohne deinen eigenen Durchlauf.' }));
        const lo = 55, hi = 145, pos = (v) => ((Math.max(lo, Math.min(hi, v)) - lo) / (hi - lo)) * 100;
        const liste = h('ul', { class: 'thct-dots thct-dots--vergleich' });
        for (const g of c.gruppen) {
          let satz;
          if (!g.n) satz = 'noch niemand außer dir';
          else {
            const pct = g.n >= 30 ? ', höher als ' + Math.round((g.darunter / g.n) * 100) + ' %' : '';
            satz = 'Ø ' + NF0.format(g.mittel) + ' bei ' + NF0.format(g.n) + (g.n === 1 ? ' anderen Person' : ' anderen') + ' · du liegst über ' + NF0.format(g.darunter) + ' von ' + NF0.format(g.n) + pct;
          }
          const zeile = h('li', { class: 'thct-dots__zeile' + (g.n && g.n < 30 ? ' is-wenig' : ''), tabindex: '0', 'aria-label': g.label + ': ' + satz + '. Dein Wert ' + r.iq + '.' },
            h('span', { class: 'thct-dots__name', text: g.label }),
            h('span', { class: 'thct-dots__spur', 'aria-hidden': 'true' },
              h('span', { class: 'thct-dots__ref', style: 'left:' + pos(100) + '%' }),
              g.mittel !== null && g.mittel !== undefined ? h('span', { class: 'thct-dots__punkt', style: 'left:' + pos(g.mittel) + '%' }) : null,
              h('span', { class: 'thct-dots__du', style: 'left:' + pos(r.iq) + '%' })),
            h('span', { class: 'thct-dots__wert', text: satz }));
          liste.append(zeile);
        }
        box.append(liste, h('p', { class: 'thct-klein thct-dots__legende', text: 'Roter Strich: du · blauer Punkt: Mittelwert der anderen · graue Linie: 100. Bei weniger als 30 anderen (hohler Punkt) hängt der Mittelwert stark vom Zufall ab.' }));
      } else {
        if (c.besserAlsProzent !== null) box.append(h('p', null, 'Du hast einen höheren Wert als ', h('b', { text: c.besserAlsProzent + ' %' }), ' der bisher ' + NF0.format(c.n) + ' gewerteten Teilnahmen.'));
        else box.append(h('p', { text: 'Der Vergleich erscheint ab 30 gewerteten Teilnahmen. Bisher sind es ' + NF0.format(c.n) + '.' }));
        if (c.alter) {
          if (c.alter.besserAlsProzent !== null) box.append(h('p', null, 'In deiner Altersgruppe (' + c.alter.label + '): höher als ', h('b', { text: c.alter.besserAlsProzent + ' %' }), ' (n = ' + NF0.format(c.alter.n) + ').'));
          else box.append(h('p', { class: 'thct-klein', text: 'Vergleich in deiner Altersgruppe (' + c.alter.label + '): noch zu wenige Daten.' }));
        }
      }
      // nicht „anonym“: ohne Namen und Anmeldung trifft es genauer (QA-Runde 3, Opus H-1)
      box.append(h('p', { class: 'thct-klein', text: 'Die Teilnehmenden haben sich selbst für den Test entschieden, die Gruppe ist nicht repräsentativ. ' + (r.inStatistik ? 'Dein Durchlauf fließt ohne Namen in die Statistik unten ein.' : 'Dein Durchlauf fließt nicht in die Statistik ein.') }));
      box.append(h('p', null, h('a', { href: '#iq-statistik', class: 'thct-link', text: 'Zur Statistik nach Bundesland, Ort, Alter und Geschlecht' })));
      return box;
    }

    recommendations(r) {
      const by = {};
      for (const p of this.produkte) by[p.handle] = p;
      // eigene, neutrale Kurztexte statt Produkt-Werbesätzen (Abnahme-QA: „Dein Kind lernt …“ passte nicht)
      const SATZ = {
        'iq-mensa-test': 'IQ-Aufgaben üben: Matrizen, Zahlenreihen und Logik mit Lösungen.',
        'matrizentest-online-testtrainer': 'Matrizen gezielt üben, mit Lösungsweg zu jeder Aufgabe.',
        'logik-trainer-logik-raetsel-mit-loesungen': 'Logikaufgaben und Zahlenreihen üben, mit Lösungen.',
        'iq-test-jugendliche': 'IQ-Aufgaben für Jugendliche üben, mit Lösungen.',
        'einstellungstest-vorbereitung': 'Typische Aufgaben aus Einstellungstests üben, mit Lernplan.',
        'figurenreihen-raeumliches-denken': 'Räumliches Denken und Figurenreihen üben, mit Lösungen.',
      };
      const want = [];
      const add = (hd) => { if (by[hd] && !want.includes(hd)) want.push(hd); };
      if (r.anlass === 'einstellungstest') add('einstellungstest-vorbereitung');
      if (r.anlass === 'mensa') add('iq-mensa-test');
      if (this.jung) add('iq-test-jugendliche');
      // Empfehlung nach Teilbereich nur bei deutlicher (gesicherter) Schwäche, nie aus Scheinunterschieden
      const schwach = (r.subscales || []).filter((sc) => sc.einordnung === 'schwaeche').map((sc) => sc.key);
      // Raum-Schwäche → Trainer für räumliches Denken (vorher Matrizen, Opus N-N6)
      if (schwach.includes('raum')) add('figurenreihen-raeumliches-denken');
      if (schwach.includes('matrizen')) add('matrizentest-online-testtrainer');
      if (schwach.includes('zahlen') || schwach.includes('logik')) add('logik-trainer-logik-raetsel-mit-loesungen');
      add('iq-mensa-test');
      add('matrizentest-online-testtrainer');
      const picks = want.slice(0, 2).map((hd) => by[hd]);
      if (!picks.length) return h('div');
      return h('section', { class: 'thct-empfehlung', 'aria-labelledby': 'thct-empf-t' },
        h('h3', { id: 'thct-empf-t', text: 'Gezielt weiter üben' }),
        h('p', { class: 'thct-klein', text: 'Wenn du dich auf einen Test vorbereitest, passen diese Trainer:' }),
        h('div', { class: 'thct-empfehlung__raster' }, ...picks.map((p) => h('a', { class: 'thct-produkt', href: p.url },
          p.bild ? h('img', { src: p.bild, alt: '', width: '96', height: '96', loading: 'lazy' }) : null,
          h('span', { class: 'thct-produkt__text' }, h('b', { text: p.titel }), h('span', { class: 'thct-klein', text: SATZ[p.handle] || '' }), h('span', { class: 'thct-produkt__preis', text: p.preis ? 'ab ' + p.preis + ' · kein Abo' : '' }))))));
    }

    shareRow(r) {
      const url = location.origin + location.pathname;
      // unter 18 ohne Zahl im Teiltext (Opus N-N10)
      const mitZahl = r.iq !== null && r.iq !== undefined && !this.jung;
      const text = mitZahl ? 'Meine vorläufige IQ-Schätzung im TestHelden IQ-Test: ' + r.iq + ' (Bereich ' + r.ciLow + ' bis ' + r.ciHigh + '). Probier ihn selbst:' : 'Ich habe den TestHelden IQ-Test gemacht. Probier ihn selbst:';
      const status = h('span', { class: 'thct-klein', 'aria-live': 'polite' });
      // Mit Ergebnisbild teilen, wo das Gerät Dateien teilen kann: Viele Apps übernehmen sonst nur den Link, nicht den
      // Text mit dem Ergebnis (Philipp 20.10.2026)
      const share = h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: async () => {
        try {
          const bild = mitZahl ? await this.ergebnisBild(r) : null;
          const datei = bild ? new File([bild], 'mein-iq-ergebnis.png', { type: 'image/png' }) : null;
          if (datei && navigator.canShare && navigator.canShare({ files: [datei] })) await navigator.share({ files: [datei], title: 'IQ-Test', text: text + ' ' + url });
          else if (navigator.share) await navigator.share({ title: 'IQ-Test', text, url });
          else { await navigator.clipboard.writeText(text + ' ' + url); status.textContent = 'Text mit Ergebnis und Link kopiert.'; }
        } catch (e) { /* abgebrochen */ }
      } }, 'Ergebnis teilen');
      const speichern = mitZahl ? h('button', { type: 'button', class: 'thct-link', onclick: async () => {
        const bild = await this.ergebnisBild(r);
        if (!bild) return;
        const a = h('a', { href: URL.createObjectURL(bild), download: 'mein-iq-ergebnis.png' });
        document.body.append(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
        status.textContent = 'Bild gespeichert.';
      } }, 'Als Bild speichern') : null;
      const again = h('button', { type: 'button', class: 'thct-link', onclick: () => this.neuStart() }, 'Test erneut machen');
      return h('div', { class: 'thct-aktionen thct-aktionen--teilen' }, share, speichern, status, again);
    }

    /** Ergebniskarte als PNG (1080 × 1350, Format für Story und Feed); nur vorläufige Schätzung mit Bereich */
    async ergebnisBild(r) {
      try {
        const W = 1080, H = 1350;
        const c = document.createElement('canvas');
        c.width = W;
        c.height = H;
        const g = c.getContext('2d');
        if (!g) return null;
        try { await Promise.all([document.fonts.load('700 120px Barlow'), document.fonts.load('400 40px "PT Sans"')]); } catch (e) { /* Ersatzschrift */ }
        const KOPF = 'Barlow, "Arial Narrow", Arial, sans-serif';
        const TEXT = '"PT Sans", Arial, sans-serif';
        g.fillStyle = '#00214E';
        g.fillRect(0, 0, W, H);
        g.fillStyle = '#F15E4C';
        g.fillRect(80, 110, 120, 12);
        g.fillStyle = '#FFFFFF';
        g.font = '700 60px ' + KOPF;
        g.fillText('Meine vorläufige', 80, 220);
        g.fillText('IQ-Schätzung', 80, 292);
        g.font = '700 300px ' + KOPF;
        g.fillText(String(r.iq), 66, 600);
        g.fillStyle = '#C2CFE2';
        g.font = '400 46px ' + TEXT;
        g.fillText('95-%-Bereich ' + r.ciLow + ' bis ' + r.ciHigh, 80, 680);
        // Glockenkurve 55 bis 145
        const L = 80, R = W - 80, T = 760, B = 1060;
        const x = (v) => L + ((v - 55) / 90) * (R - L);
        const y = (v) => B - (normalPdf(v, 100, 15) / normalPdf(100, 100, 15)) * (B - T);
        g.beginPath();
        g.moveTo(x(Math.max(55, r.ciLow)), B);
        for (let v = Math.max(55, r.ciLow); v <= Math.min(145, r.ciHigh); v++) g.lineTo(x(v), y(v));
        g.lineTo(x(Math.min(145, r.ciHigh)), B);
        g.closePath();
        g.fillStyle = 'rgba(194, 207, 226, 0.35)';
        g.fill();
        g.beginPath();
        for (let v = 55; v <= 145; v++) (v === 55 ? g.moveTo(x(v), y(v)) : g.lineTo(x(v), y(v)));
        g.strokeStyle = '#FFFFFF';
        g.lineWidth = 5;
        g.stroke();
        g.fillStyle = 'rgba(255, 255, 255, 0.55)';
        g.fillRect(L, B, R - L, 3);
        g.font = '400 30px ' + TEXT;
        g.fillStyle = '#C2CFE2';
        g.textAlign = 'center';
        for (const t of [70, 85, 100, 115, 130]) g.fillText(String(t), x(t), B + 48);
        const iq = Math.max(55, Math.min(145, r.iq));
        g.strokeStyle = '#F15E4C';
        g.lineWidth = 6;
        g.beginPath();
        g.moveTo(x(iq), T - 20);
        g.lineTo(x(iq), B);
        g.stroke();
        g.fillStyle = '#F15E4C';
        g.beginPath();
        g.arc(x(iq), y(iq), 16, 0, Math.PI * 2);
        g.fill();
        g.textAlign = 'left';
        g.fillStyle = '#FFFFFF';
        g.font = '700 46px ' + KOPF;
        g.fillText('TestHelden IQ-Test', 80, 1215);
        g.fillStyle = '#C2CFE2';
        g.font = '400 34px ' + TEXT;
        g.fillText('testhelden.com · kostenlos, ohne Anmeldung', 80, 1268);
        return await new Promise((res) => c.toBlob(res, 'image/png'));
      } catch (e) {
        return null;
      }
    }

    /** Ergebnisübersicht als PDF per E-Mail; Einwilligung wie bei den Blog-Quizzes */
    pdfBox() {
      const status = h('p', { class: 'thct-klein thct-pdf__status', role: 'status', 'aria-live': 'polite' });
      const input = h('input', { class: 'thct-input', type: 'email', id: 'thct-pdf-mail', name: 'email', autocomplete: 'email', autocapitalize: 'off', spellcheck: 'false', required: true, 'aria-describedby': 'thct-pdf-einw' });
      const btn = h('button', { type: 'submit', class: 'th-btn th-btn--primary' }, 'PDF anfordern');
      const form = h('form', { class: 'thct-pdf__form', novalidate: true },
        h('label', { class: 'thct-label', for: 'thct-pdf-mail', text: 'E-Mail-Adresse' }),
        h('div', { class: 'thct-pdf__zeile' }, input, btn));
      form.addEventListener('submit', async (ev) => {
        ev.preventDefault();
        const email = input.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          status.textContent = 'Bitte gib eine gültige E-Mail-Adresse ein.';
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        input.removeAttribute('aria-invalid');
        btn.disabled = true;
        status.textContent = 'Deine PDF wird erstellt …';
        try {
          await api(this.api, '/api/mobile/iq-test/ergebnis-pdf', { method: 'POST', body: JSON.stringify({ token: this.run.session.token, email, quelle: location.pathname }) });
          status.textContent = 'Verschickt an ' + email + '. Schau in dein Postfach, notfalls auch in den Spam-Ordner.';
          form.hidden = true;
        } catch (e) {
          status.textContent = e.message || 'Das hat nicht geklappt. Bitte versuche es gleich noch einmal.';
          btn.disabled = false;
        }
      });
      return h('section', { class: 'thct-pdf', 'aria-labelledby': 'thct-pdf-t' },
        h('h3', { id: 'thct-pdf-t', text: 'Dein Ergebnis als PDF' }),
        h('p', { class: 'thct-klein', text: 'Deine Ergebnisübersicht mit Schätzung, Bereich, Teilbereichen und Vergleich, kostenlos per E-Mail.' }),
        form,
        h('p', { class: 'thct-klein thct-pdf__einw', id: 'thct-pdf-einw' }, 'Mit dem Klick auf „PDF anfordern“ willigst du ein, dass wir dir deine Ergebnisübersicht sowie Übungsaufgaben, Tipps zur Vorbereitung und Angebote von TestHelden per E-Mail schicken. Abmeldung jederzeit über den Link in jeder Mail. ', h('a', { href: '/policies/privacy-policy', text: 'Datenschutzerklärung' })),
        status);
    }

    review(r) {
      const items = {};
      for (const b of this.blocks) for (const it of b.items) items[it.id] = it;
      const det = h('details', { class: 'thct-loesungen' }, h('summary', null, 'Lösungen ansehen (' + r.raw + ' von ' + r.max + ' richtig)'));
      const list = h('ol', { class: 'thct-loesungen__liste' });
      for (const x of r.review || []) {
        const it = items[x.id];
        if (!it) continue;
        const li = h('li', { class: 'thct-loesung ' + (x.richtig ? 'is-richtig' : 'is-falsch') });
        li.append(h('p', { class: 'thct-loesung__kopf' }, h('b', { text: (x.richtig ? '✓ Richtig' : x.zeitabgelaufen ? '○ Zeit abgelaufen' : '✗ Nicht richtig') }), ' · ' + it.prompt));
        li.append(this.renderStimulus(it, true));
        li.append(this.renderOptionsStatic(it, x.loesung, x.antwort));
        const deine = x.antwort === null || x.antwort === undefined ? 'keine' : this.solutionLabel(it, x.antwort);
        li.append(h('p', { class: 'thct-klein' }, 'Deine Antwort: ' + deine + ' · Lösung: ', h('b', { text: this.solutionLabel(it, x.loesung) })));
        if (x.erklaerung) li.append(h('p', { class: 'thct-loesung__erkl', text: x.erklaerung }));
        list.append(li);
      }
      det.append(list);
      return det;
    }

    solutionLabel(item, sol) {
      if (sol === null || sol === undefined) return '–';
      if (item.format.kind === 'number') return NF0.format(Number(sol));
      const ids = Array.isArray(sol) ? sol : String(sol).split(',');
      return ids.map((id) => {
        const i = item.options.findIndex((o) => o.id === id);
        const o = item.options[i];
        return i < 0 ? id : LETTERS[i] + (o && o.text ? ' (' + o.text + ')' : '');
      }).join(' und ');
    }

    renderOptionsStatic(item, sol, mine) {
      if (item.format.kind === 'number') return h('div');
      const solIds = Array.isArray(sol) ? sol : String(sol).split(',');
      const mineIds = mine === null || mine === undefined ? [] : Array.isArray(mine) ? mine : [String(mine)];
      const grid = h('div', { class: 'thct-optionen thct-optionen--statisch thct-optionen--' + item.type + ' thct-optionen--n' + item.options.length });
      item.options.forEach((o, i) => {
        const cls = 'thct-option' + (o.svg ? ' thct-option--bild' : ' thct-option--text') + (solIds.includes(o.id) ? ' is-loesung' : '') + (mineIds.includes(o.id) && !solIds.includes(o.id) ? ' is-falsch' : '');
        grid.append(h('div', { class: cls }, h('span', { class: 'thct-option__buchstabe', text: LETTERS[i] }), o.svg ? h('span', { class: 'thct-option__bild', html: o.svg }) : h('span', { class: 'thct-option__text', text: o.text })));
      });
      return grid;
    }
  }

  // ── Statistik ──────────────────────────────────────────────────────────
  const CAT = { w: '#3A6AAE', m: '#F15E4C', d: '#8A6D00' }; // dataviz-Validator: alle Paare bestanden (w↔m CVD ΔE 18; d nur mit Legende + Werten, ΔE 6,4)
  const RAMP = ['#E3E9F2', '#C2CFE2', '#97ABCB', '#6C87B2', '#456696', '#244878', '#0B2C5C'];
  const RAMP_MIN = 94, RAMP_MAX = 106;

  class ThCogtestStats extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.setAttribute('data-thct-version', VERSION);
      this.api = (this.dataset.api || 'https://app.testhelden.com').replace(/\/$/, '');
      this.instrument = this.dataset.instrument || 'th-iq';
      this.karteUrl = this.dataset.karte;
      this.out = this.querySelector('[data-thct-statistik]');
      const go = () => this.load();
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); go(); } }, { rootMargin: '600px' });
        io.observe(this);
      } else go();
      document.addEventListener('thct:ergebnis', () => setTimeout(() => this.load(true), 1500));
    }

    async load(again) {
      try {
        const [data, karte] = await Promise.all([
          api(this.api, '/api/mobile/iq-test/stats?instrument=' + encodeURIComponent(this.instrument) + (again ? '&t=' + Date.now() : ''), { method: 'GET', headers: {} }),
          this.karte || !this.karteUrl ? Promise.resolve(this.karte) : fetch(this.karteUrl).then((r) => r.json()).catch(() => null),
        ]);
        this.karte = karte;
        this.render(data);
      } catch (e) {
        this.out.replaceChildren(h('p', { class: 'thct-klein', text: 'Die Statistik ist gerade nicht erreichbar. Bitte später noch einmal versuchen.' }));
      }
    }

    tip() {
      if (!this._tip) {
        this._tip = h('div', { class: 'thct-tooltip', role: 'tooltip', hidden: true });
        document.body.append(this._tip);
      }
      return this._tip;
    }
    bindTip(el, lines) {
      const show = (ev) => {
        const t = this.tip();
        t.replaceChildren(...lines.map((l, i) => h(i === 0 ? 'b' : 'span', { text: l })));
        t.hidden = false;
        const r = el.getBoundingClientRect();
        const px = ev && ev.clientX ? ev.clientX : r.left + r.width / 2;
        const py = ev && ev.clientY ? ev.clientY : r.top;
        const tw = t.offsetWidth;
        t.style.left = Math.max(8, Math.min(window.innerWidth - tw - 8, px - tw / 2)) + 'px';
        t.style.top = (py + window.scrollY - t.offsetHeight - 12) + 'px';
      };
      const hide = () => { if (this._tip) this._tip.hidden = true; };
      el.addEventListener('pointermove', show);
      el.addEventListener('pointerleave', hide);
      el.addEventListener('focus', () => show(null));
      el.addEventListener('blur', hide);
    }

    chartW() { return Math.round(Math.min(860, Math.max(300, (this.out && this.out.clientWidth) || 640))); }
    mw(g) { return g.mean === null ? null : NF0.format(g.mean); }
    // an den Grenzen 55 und 145 ist das Intervall abgeschnitten, nicht berechnet
    ki(g) { return (g.ciLow <= 55 ? 'unter 55' : NF0.format(g.ciLow)) + ' bis ' + (g.ciHigh >= 145 ? 'über 145' : NF0.format(g.ciHigh)); }
    nTxt(g) { return g.n === null || g.n === undefined ? 'unter 30' : NF0.format(g.n); }

    render(d) {
      const stand = new Date(d.stand).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
      const nodes = [];
      // ältere Plattform-Fassung: Zähler auf Zehner abgerundet („rund 40“), seit 20.10.2026 genau
      const zaehler = (n, gerundet) => (!gerundet ? NF0.format(n) : n < 10 ? 'weniger als 10' : 'rund ' + NF0.format(n));
      const neu = typeof d.anzeigeAb === 'number';
      nodes.push(h('dl', { class: 'thct-kpi' },
        h('div', null, h('dt', { text: 'Abgeschlossene Tests' }), h('dd', { text: zaehler(d.runsTotal, d.runsTotalGerundet) })),
        h('div', null, h('dt', { text: 'Davon in der Statistik' }), h('dd', { text: zaehler(d.inStats, d.inStatsGerundet) })),
        h('div', null, h('dt', { text: 'Mittelwert aller Gewerteten' }), h('dd', { text: d.gesamt.mean === null ? '–' : NF0.format(d.gesamt.mean) }), d.gesamt.mean === null ? null : h('dd', { class: 'thct-klein', text: d.gesamt.ciLow === null ? 'eine Teilnahme' : '95-%-Bereich ' + this.ki(d.gesamt) })),
        h('div', null, h('dt', { text: 'Stand' }), h('dd', { class: 'thct-kpi__klein', text: stand }))));
      nodes.push(this.mitmachen());
      if (!neu && d.inStats < d.minN) {
        // ältere Plattform-Fassung: Leerzustand bis 30
        nodes.push(h('div', { class: 'thct-leer' }, h('p', null, h('b', { text: zaehler(d.inStats, d.inStatsGerundet).replace(/^w/, 'W').replace(/^r/, 'R') + ' von ' + d.minN }), ' gewerteten Teilnahmen bis zur ersten Auswertung.')));
      } else if (!d.inStats) {
        nodes.push(h('p', { class: 'thct-leer', text: 'Noch keine gewertete Teilnahme. Mach den Test und sei die erste Person in der Statistik.' }));
      } else {
        if (d.inStats < d.minN) nodes.push(h('p', { class: 'thct-hinweis-wenig', text: 'Noch wenige Daten: Wir zeigen jede Gruppe ab der ersten Teilnahme. Bei kleinen Gruppen hängt ein Mittelwert stark vom Zufall ab, bei einer Teilnahme ist es das Ergebnis einer einzelnen Person. Deshalb steht bei jeder Zahl die Anzahl n und, wo möglich, der 95-%-Bereich; Gruppen unter 30 haben einen hohlen Punkt und sind auf der Karte blass.' }));
        nodes.push(this.bundesland(d));
        if (Array.isArray(d.orte)) nodes.push(this.orte(d));
        nodes.push(this.geschlecht(d));
        nodes.push(this.alter(d));
        nodes.push(this.verteilung(d));
        // weitere Kohorten zusammengeklappt, damit die Seite am Handy nicht endlos wird (QA Fable M3)
        const mehr = [];
        if (Array.isArray(d.ortsgroesse)) mehr.push(this.kohorte('thct-st-og', 'Großstadt oder Land', d.ortsgroesse, 'Nach Größe des Wohnorts (aus der Postleitzahl).'));
        mehr.push(this.kohorte('thct-st-b', 'Nach höchstem Abschluss', d.bildung));
        if (Array.isArray(d.muttersprache)) mehr.push(this.kohorte('thct-st-ms', 'Nach Muttersprache', d.muttersprache, 'Die Aufgaben kommen fast ohne Sprache aus; Unterschiede zeigen eher, wer am Test teilnimmt.'));
        if (Array.isArray(d.vorerfahrung)) mehr.push(this.kohorte('thct-st-ve', 'Mit oder ohne Erfahrung mit IQ-Tests', d.vorerfahrung, 'Wer solche Aufgaben kennt, löst sie im Mittel etwas besser (Übungseffekt).'));
        if (Array.isArray(d.anlass)) mehr.push(this.kohorte('thct-st-an', 'Nach Anlass', d.anlass));
        if (Array.isArray(d.geraet)) mehr.push(this.kohorte('thct-st-ge', 'Handy oder Computer', d.geraet, 'Auf kleinen Bildschirmen sind die Figuren kleiner; ein Unterschied kann am Gerät oder an der Gruppe liegen.'));
        nodes.push(h('details', { class: 'thct-mehr' }, h('summary', { text: 'Weitere Vergleiche: Ortsgröße, Abschluss, Muttersprache, Erfahrung, Anlass, Gerät' }), ...mehr));
      }
      if (neu) nodes.push(h('p', { class: 'thct-klein thct-methode' },
        'Gewertet werden Erstdurchläufe ohne Auffälligkeiten (z. B. kein Durchklicken, nicht unter 4 Minuten, keine unplausiblen Zeiten und nicht mehrere Abgaben aus demselben Netz am selben Tag), ohne Testläufe und nur, wenn der Statistik nicht widersprochen wurde. Bundesland, Ort und Ortsgröße zählen nur, wenn sie aus der Postleitzahl stammen. Werte sind auf ganze Punkte gerundet. Jede Gruppe erscheint ab der ersten Teilnahme mit Anzahl n; der 95-%-Bereich folgt der t-Verteilung und ist bei kleinen Gruppen entsprechend breit. ',
        d.gewichtet.verfuegbar ? 'Gewichteter Mittelwert (angeglichen an Alter, Geschlecht und Bundesland laut Zensus 2022, Stand 31.12.2025): ' + NF0.format(d.gewichtet.mean) + ' (95-%-KI ' + NF0.format(d.gewichtet.ciLow) + ' bis ' + NF0.format(d.gewichtet.ciHigh) + ', effektives n = ' + NF0.format(d.gewichtet.nEff) + '). ' : d.gewichtet.grund + ' ',
        'Stand: ' + stand + '.'));
      this.out.replaceChildren(...nodes);
    }

    leer(n, was) {
      return h('p', { class: 'thct-leer', text: 'Noch keine Daten' + (was ? ' für ' + was : '') + '.' });
    }

    mitmachen() {
      const url = location.origin + location.pathname;
      const text = 'Mach mit beim TestHelden IQ-Test und sieh, wie du im Vergleich abschneidest:';
      const status = h('span', { class: 'thct-klein', 'aria-live': 'polite' });
      const test = document.getElementById('iq-test');
      return h('div', { class: 'thct-aktionen thct-aktionen--mitmachen' },
        test ? h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => scrollIntoViewTop(test) }, 'Selbst testen') : null,
        h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: async () => {
          try {
            if (navigator.share) await navigator.share({ title: 'IQ-Test', text, url });
            else { await navigator.clipboard.writeText(text + ' ' + url); status.textContent = 'Link kopiert.'; }
          } catch (e) { /* abgebrochen */ }
        } }, 'Test teilen'),
        status);
    }

    tabelle(caption, head, rows) {
      return h('details', { class: 'thct-tabelle' }, h('summary', { text: 'Zahlen als Tabelle' }),
        h('table', null, h('caption', { class: 'th-sr-only', text: caption }),
          h('thead', null, h('tr', null, ...head.map((x) => h('th', { scope: 'col', text: x })))),
          h('tbody', null, ...rows.map((r) => h('tr', null, ...r.map((x, i) => (i === 0 ? h('th', { scope: 'row', text: x }) : h('td', { text: x }))))))));
    }

    kiTxt(g) { return g.mean === null ? '–' : g.ciLow === null ? 'eine Teilnahme' : this.ki(g); }
    zeilen(groups) { return groups.map((g) => [g.label, this.nTxt(g), g.mean === null ? 'keine Daten' : NF0.format(g.mean), this.kiTxt(g)]); }
    wenig(g) { return g.belastbar === false || (typeof g.n === 'number' && g.n < 30); }

    /** Punkt-und-Intervall-Liste (statt Balken: IQ hat keinen sinnvollen Nullpunkt); hell = unter 30 Teilnahmen */
    dotList(groups, opts) {
      const shown = groups.filter((g) => g.mean !== null);
      if (!shown.length) return null;
      // mindestens 85 bis 115 (eine Standardabweichung), sonst füllt bei Einzelwerten 1 Punkt die ganze Breite (QA Opus M4)
      const lo = Math.min(...shown.map((g) => (g.ciLow === null ? g.mean : g.ciLow)), 85) - 1;
      const hi = Math.max(...shown.map((g) => (g.ciHigh === null ? g.mean : g.ciHigh)), 115) + 1;
      const pos = (v) => ((v - lo) / (hi - lo)) * 100;
      const list = h('ul', { class: 'thct-dots' });
      const sorted = opts && opts.sort ? shown.slice().sort((a, b) => b.mean - a.mean || (b.n || 0) - (a.n || 0)) : shown;
      for (const g of sorted) {
        const ki = g.ciLow === null ? 'eine Teilnahme' : '95-%-Bereich ' + this.ki(g);
        const row = h('li', { class: 'thct-dots__zeile' + (this.wenig(g) ? ' is-wenig' : ''), tabindex: '0', 'aria-label': g.label + ': Mittelwert ' + NF0.format(g.mean) + ', ' + ki + ', n = ' + this.nTxt(g) + (this.wenig(g) ? ', wenig Daten' : '') },
          h('span', { class: 'thct-dots__name', text: g.label }),
          h('span', { class: 'thct-dots__spur', 'aria-hidden': 'true' },
            h('span', { class: 'thct-dots__ref', style: 'left:' + pos(100) + '%' }),
            g.ciLow === null ? null : h('span', { class: 'thct-dots__ki', style: 'left:' + pos(g.ciLow) + '%;width:' + (pos(g.ciHigh) - pos(g.ciLow)) + '%' }),
            h('span', { class: 'thct-dots__punkt', style: 'left:' + pos(g.mean) + '%' })),
          h('span', { class: 'thct-dots__wert', text: NF0.format(g.mean) + ' (n = ' + this.nTxt(g) + ')' }));
        this.bindTip(row, [g.label, 'Mittelwert ' + NF0.format(g.mean), ki, 'n = ' + this.nTxt(g) + (this.wenig(g) ? ' (wenig Daten)' : '')]);
        list.append(row);
      }
      return h('div', { class: 'thct-dots-wrap' }, list, h('p', { class: 'thct-klein thct-dots__legende', text: 'Punkt: Mittelwert · Balken: 95-%-Bereich · senkrechte Linie: 100 · hohler Punkt: unter 30 Teilnahmen' }));
    }

    kohorte(id, titel, groups, text) {
      const sec = h('section', { class: 'thct-stat', 'aria-labelledby': id }, h('h3', { id, text: titel }));
      if (text) sec.append(h('p', { class: 'thct-klein thct-stat__unter', text }));
      sec.append(this.dotList(groups, { sort: false }) || this.leer(null, ''));
      const ohne = groups.filter((g) => g.mean === null).map((g) => g.label);
      if (ohne.length && ohne.length < groups.length) sec.append(h('p', { class: 'thct-klein', text: 'Noch keine Teilnahme: ' + ohne.join(', ') + '.' }));
      sec.append(this.tabelle(titel, ['Gruppe', 'n', 'Mittelwert', '95-%-Bereich'], this.zeilen(groups)));
      return sec;
    }

    bundesland(d) {
      const sec = h('section', { class: 'thct-stat', 'aria-labelledby': 'thct-st-bl' }, h('h3', { id: 'thct-st-bl', text: 'Nach Bundesland' }));
      const groups = d.bundesland;
      const byCode = {};
      for (const g of groups) byCode[g.key] = g;
      const grid = h('div', { class: 'thct-stat__zwei' });
      if (this.karte && this.karte.laender) {
        const color = (m) => { const t = Math.max(0, Math.min(1, (m - RAMP_MIN) / (RAMP_MAX - RAMP_MIN))); return RAMP[Math.min(RAMP.length - 1, Math.floor(t * RAMP.length))]; };
        const svg = s('svg', { viewBox: this.karte.viewBox, class: 'thct-karte', role: 'group', 'aria-label': 'Deutschlandkarte: Mittelwert je Bundesland' },
          s('defs', null, s('pattern', { id: 'thct-schraffur', width: 8, height: 8, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' }, s('rect', { width: 8, height: 8, fill: '#F2F4F7' }), s('line', { x1: 0, y1: 0, x2: 0, y2: 8, stroke: '#C9D1DC', 'stroke-width': 2 }))));
        for (const l of this.karte.laender) {
          const g = byCode[l.code] || { n: 0, mean: null, label: l.name };
          const ki = g.mean === null ? '' : g.ciLow === null ? 'eine Teilnahme' : '95-%-Bereich ' + this.ki(g);
          const path = s('path', { d: l.d, 'fill-rule': 'evenodd', class: 'thct-karte__land', fill: g.mean === null ? 'url(#thct-schraffur)' : color(g.mean), 'fill-opacity': g.mean !== null && this.wenig(g) ? '0.45' : null, tabindex: '0', role: 'img',
            'aria-label': g.mean === null ? l.name + ': noch keine Teilnahme' : l.name + ': Mittelwert ' + NF0.format(g.mean) + ', ' + ki + ', n = ' + this.nTxt(g) });
          this.bindTip(path, g.mean === null ? [l.name, 'Noch keine Teilnahme'] : [l.name, 'Mittelwert ' + NF0.format(g.mean), ki, 'n = ' + this.nTxt(g) + (this.wenig(g) ? ' (wenig Daten)' : '')]);
          svg.append(path);
        }
        const legend = h('div', { class: 'thct-karte__legende', 'aria-hidden': 'true' },
          h('span', { class: 'thct-karte__verlauf', style: 'background:linear-gradient(90deg,' + RAMP.join(',') + ')' }),
          h('span', { class: 'thct-karte__ticks' }, h('span', { text: '≤ ' + RAMP_MIN }), h('span', { text: '100' }), h('span', { text: '≥ ' + RAMP_MAX })),
          h('span', { class: 'thct-karte__leer' }, h('i'), 'noch keine Teilnahme'),
          h('span', { class: 'thct-karte__leer thct-karte__blass' }, h('i'), 'blass: unter 30 Teilnahmen'));
        grid.append(h('figure', { class: 'thct-karte-wrap' }, svg, legend,
          h('figcaption', { class: 'thct-klein' }, 'Feste Farbskala von ' + RAMP_MIN + ' bis ' + RAMP_MAX + ', damit kleine Unterschiede nicht größer wirken, als sie sind. Karte: © ', h('a', { href: 'https://www.bkg.bund.de', rel: 'noopener', text: 'BKG' }), ' (2026) ', h('a', { href: 'https://www.govdata.de/dl-de/by-2-0', rel: 'noopener', text: 'dl-de/by-2-0' }), ', ', h('a', { href: 'https://sgx.geodatenzentrum.de/web_public/gdz/datenquellen/datenquellen_vg_nuts.pdf', rel: 'noopener', text: 'Datenquellen' }), '; Geometrien umprojiziert und vereinfacht.')));
      }
      const dl = this.dotList(groups, { sort: true });
      const ohne = groups.filter((g) => g.mean === null);
      const right = h('div', { class: 'thct-stat__liste' });
      if (dl) right.append(h('p', { class: 'thct-stat__unter', text: 'Rangfolge mit Unsicherheit' }), dl);
      else right.append(this.leer(null, 'die Bundesländer'));
      if (ohne.length && dl) right.append(h('p', { class: 'thct-klein', text: 'Noch keine Teilnahme: ' + ohne.map((g) => g.label).join(', ') + '.' }));
      right.append(h('p', { class: 'thct-klein', text: 'Überlappen sich die Bereiche zweier Länder, ist ein Unterschied nicht belegt. Für belastbare Vergleiche braucht es einige hundert Teilnahmen je Land.' }));
      grid.append(right);
      sec.append(grid, this.tabelle('Mittelwert je Bundesland', ['Bundesland', 'n', 'Mittelwert', '95-%-Bereich'], this.zeilen(groups)));
      return sec;
    }

    orte(d) {
      const sec = h('section', { class: 'thct-stat', 'aria-labelledby': 'thct-st-o' }, h('h3', { id: 'thct-st-o', text: 'Nach Ort' }));
      // gleichnamige Orte im selben Land (z. B. zwei Burgdorf in Niedersachsen) über den Kreisschlüssel unterscheiden
      const name = (g) => g.label + (g.bundesland ? ' (' + g.bundesland + ')' : '');
      const zahl = {};
      for (const g of d.orte) zahl[name(g)] = (zahl[name(g)] || 0) + 1;
      const kreis = (g) => String(g.key).split('|')[0];
      const orte = d.orte.filter((g) => g.mean !== null).map((g) => Object.assign({}, g, { label: zahl[name(g)] > 1 ? g.label + ' (' + (g.bundesland ? g.bundesland + ', ' : '') + 'Kreis ' + kreis(g) + ')' : name(g) }));
      if (!orte.length) {
        sec.append(this.leer(null, 'Orte'), h('p', { class: 'thct-klein', text: 'Orte zählen seit dem 20.10.2026, wenn beim Test die Postleitzahl angegeben wurde.' }));
        return sec;
      }
      const TOP = 15;
      const k = Math.min(TOP, orte.length);
      sec.append(h('p', { class: 'thct-klein thct-stat__unter', text: (k === 1 ? 'Bisher ein Ort' : 'Die ' + k + ' Orte mit den meisten Teilnahmen') + (d.orteGesamt > orte.length ? ' (von ' + NF0.format(d.orteGesamt) + ')' : '') + '. Ort laut Postleitzahl; teilen sich mehrere Gemeinden eine Postleitzahl, zählt die größte.' }),
        this.dotList(orte.slice(0, TOP), { sort: false }));
      // alle Orte als durchsuchbare Tabelle
      const tbody = h('tbody');
      const status = h('p', { class: 'thct-klein', 'aria-live': 'polite' });
      const fuellen = (q) => {
        const t = q.trim().toLocaleLowerCase('de');
        const treffer = orte.filter((g) => !t || g.label.toLocaleLowerCase('de').includes(t));
        tbody.replaceChildren(...treffer.slice(0, 200).map((g) => h('tr', null, h('th', { scope: 'row', text: g.label }), h('td', { text: this.nTxt(g) }), h('td', { text: NF0.format(g.mean) }), h('td', { text: this.kiTxt(g) }))));
        status.textContent = treffer.length ? NF0.format(treffer.length) + (treffer.length === 1 ? ' Ort' : ' Orte') + (treffer.length > 200 ? ', die ersten 200' : '') : 'Kein Ort gefunden.';
      };
      const suche = h('input', { type: 'search', class: 'thct-input thct-orte__suche', placeholder: 'Ort suchen, z. B. Leipzig', 'aria-label': 'Ort suchen', autocomplete: 'off' });
      suche.addEventListener('input', () => fuellen(suche.value));
      const alleDa = !(d.orteGesamt > orte.length);
      sec.append(h('details', { class: 'thct-tabelle' }, h('summary', { text: alleDa ? (orte.length === 1 ? 'Ort als Tabelle' : 'Alle ' + NF0.format(orte.length) + ' Orte durchsuchen') : 'Die ' + NF0.format(orte.length) + ' Orte mit den meisten Teilnahmen durchsuchen (von ' + NF0.format(d.orteGesamt) + ')' }),
        suche, status,
        h('table', null, h('caption', { class: 'th-sr-only', text: 'Mittelwert je Ort' }),
          h('thead', null, h('tr', null, ...['Ort', 'n', 'Mittelwert', '95-%-Bereich'].map((x) => h('th', { scope: 'col', text: x })))), tbody)));
      fuellen('');
      return sec;
    }

    geschlecht(d) {
      const sec = h('section', { class: 'thct-stat', 'aria-labelledby': 'thct-st-g' }, h('h3', { id: 'thct-st-g', text: 'Frauen und Männer' }));
      const dl = this.dotList(d.geschlecht, { sort: false });
      sec.append(dl || this.leer(null, 'diesen Vergleich'));
      const shown = d.geschlecht.filter((g) => g.dichte);
      if (shown.length) {
        const W = this.chartW(), H = 230, L = 36, R = 16, T = 12, B = 190;
        const x = (v) => L + ((v - 55) / 90) * (W - L - R);
        const maxY = Math.max(...shown.flatMap((g) => g.dichte.map((p) => p[1])));
        const y = (v) => B - (v / maxY) * (B - T);
        const svg = s('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'thct-dichte', role: 'img', 'aria-label': 'Verteilung der Werte nach Geschlecht als geglättete Kurven' });
        svg.append(s('line', { x1: L, x2: W - R, y1: B, y2: B, class: 'thct-achse' }));
        for (const t of [70, 85, 100, 115, 130]) svg.append(s('line', { x1: x(t), x2: x(t), y1: T, y2: B, class: 'thct-gitter' }), s('text', { x: x(t), y: B + 18, 'text-anchor': 'middle', class: 'thct-achsentext' }, String(t)));
        for (const g of shown) {
          const pts = g.dichte.map((p) => x(p[0]).toFixed(1) + ',' + y(p[1]).toFixed(1));
          svg.append(s('path', { d: 'M' + x(g.dichte[0][0]) + ',' + B + 'L' + pts.join('L') + 'L' + x(g.dichte[g.dichte.length - 1][0]) + ',' + B + 'Z', fill: CAT[g.key], 'fill-opacity': '0.1', stroke: 'none' }));
          svg.append(s('path', { d: 'M' + pts.join('L'), fill: 'none', stroke: CAT[g.key], 'stroke-width': 2, 'stroke-linejoin': 'round' }));
          svg.append(s('line', { x1: x(g.mean), x2: x(g.mean), y1: T, y2: B, stroke: CAT[g.key], 'stroke-width': 2 }));
          const breite = g.ciLow === null ? 0 : x(g.ciHigh) - x(g.ciLow);
          const hit = s('rect', { x: x(g.ciLow === null ? g.mean : g.ciLow) - 6, y: T, width: Math.max(12, breite + 12), height: B - T, fill: 'transparent', tabindex: '0', 'aria-label': g.label + ': Mittelwert ' + NF0.format(g.mean) + ', n = ' + this.nTxt(g) });
          this.bindTip(hit, [g.label, 'Mittelwert ' + NF0.format(g.mean), this.kiTxt(g), 'Streuung (SD) ' + (g.sd === null ? '–' : NF0.format(g.sd)), 'n = ' + this.nTxt(g)]);
          svg.append(hit);
        }
        const legend = h('ul', { class: 'thct-legende' }, ...shown.map((g) => h('li', null, h('i', { style: 'background:' + CAT[g.key] }), g.label + ': Ø ' + NF0.format(g.mean) + ', n = ' + NF0.format(g.n))));
        sec.append(h('figure', { class: 'thct-fig' }, legend, svg, h('figcaption', { class: 'thct-klein', text: 'Geglättete Verteilung der Werte (ab 5 Teilnahmen je Gruppe), senkrechte Linie = Mittelwert. Fläche je Kurve = 100 % der jeweiligen Gruppe.' })));
      }
      sec.append(h('p', { class: 'thct-klein', text: 'Einordnung: In großen Studien unterscheiden sich Frauen und Männer in der allgemeinen Intelligenz im Mittel kaum (z. B. Deary et al. 2003, 2007). Gefunden wird eher eine etwas größere Streuung bei Männern und Unterschiede in einzelnen Aufgabenarten, etwa beim räumlichen Vorstellen (Halpern et al. 2007). Diese Seite misst eine selbst ausgewählte Online-Gruppe und kann solche Fragen nicht klären.' }));
      sec.append(this.tabelle('Werte nach Geschlecht', ['Geschlecht', 'n', 'Mittelwert', '95-%-Bereich', 'SD'], d.geschlecht.map((g) => this.zeilen([g])[0].concat([g.sd === null ? '–' : NF0.format(g.sd)]))));
      return sec;
    }

    alter(d) {
      const sec = h('section', { class: 'thct-stat', 'aria-labelledby': 'thct-st-a' }, h('h3', { id: 'thct-st-a', text: 'Nach Alter' }));
      const groups = d.alter;
      const shown = groups.filter((g) => g.mean !== null);
      if (!shown.length) sec.append(this.leer(null, 'die Altersgruppen'));
      else {
        const W = this.chartW(), H = 252, L = 40, R = 16, T = 14, B = 196;
        const lo = Math.floor(Math.min(...shown.map((g) => (g.ciLow === null ? g.mean : g.ciLow)), 95) - 2);
        const hi = Math.ceil(Math.max(...shown.map((g) => (g.ciHigh === null ? g.mean : g.ciHigh)), 105) + 2);
        const x = (i) => L + (i + 0.5) * ((W - L - R) / groups.length);
        const y = (v) => B - ((v - lo) / (hi - lo)) * (B - T);
        const svg = s('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'thct-alter', role: 'img', 'aria-label': 'Mittelwert je Altersgruppe mit 95-%-Bereich' });
        const step = hi - lo > 40 ? 20 : hi - lo > 20 ? 10 : 5;
        for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) svg.append(s('line', { x1: L, x2: W - R, y1: y(v), y2: y(v), class: v === 100 ? 'thct-gitter thct-gitter--100' : 'thct-gitter' }), s('text', { x: L - 6, y: y(v) + 4, 'text-anchor': 'end', class: 'thct-achsentext' }, String(v)));
        // Linie nur zwischen benachbarten Gruppen mit je mindestens 30 Teilnahmen; sonst sähe ein Zufallsunterschied
        // zwischen Einzelpersonen wie ein Alterstrend aus (QA Opus M2)
        let line = '';
        groups.forEach((g, i) => {
          const vor = groups[i - 1];
          if (g.mean !== null && !this.wenig(g) && vor && vor.mean !== null && !this.wenig(vor)) line += 'M' + x(i - 1).toFixed(1) + ',' + y(vor.mean).toFixed(1) + 'L' + x(i).toFixed(1) + ',' + y(g.mean).toFixed(1);
          svg.append(s('text', { x: x(i), y: B + 18, 'text-anchor': 'middle', class: 'thct-achsentext' }, g.label.replace(' und älter', '+').replace(' bis ', '–').replace('unter ', '< ')));
          svg.append(s('text', { x: x(i), y: B + 34, 'text-anchor': 'middle', class: 'thct-achsentext thct-achsentext--leise' }, 'n=' + (g.n === null || g.n === undefined ? '<30' : g.n)));
          if (g.mean === null) {
            svg.append(s('text', { x: x(i), y: B - 8, 'text-anchor': 'middle', class: 'thct-achsentext thct-achsentext--leise' }, 'keine'));
            return;
          }
          if (g.ciLow !== null) svg.append(s('line', { x1: x(i), x2: x(i), y1: y(g.ciLow), y2: y(g.ciHigh), stroke: '#3A6AAE', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-opacity': this.wenig(g) ? '0.45' : '1' }));
        });
        if (line) svg.append(s('path', { d: line, fill: 'none', stroke: '#3A6AAE', 'stroke-width': 2, 'stroke-linejoin': 'round' }));
        groups.forEach((g, i) => {
          if (g.mean === null) return;
          svg.append(s('circle', { cx: x(i), cy: y(g.mean), r: 5, fill: this.wenig(g) ? '#FFFFFF' : '#3A6AAE', stroke: '#3A6AAE', 'stroke-width': 2 }));
          const hit = s('rect', { x: x(i) - 20, y: T, width: 40, height: B - T, fill: 'transparent', tabindex: '0', 'aria-label': g.label + ': Mittelwert ' + NF0.format(g.mean) + ', ' + this.kiTxt(g) + ', n = ' + this.nTxt(g) });
          this.bindTip(hit, [g.label + ' Jahre', 'Mittelwert ' + NF0.format(g.mean), this.kiTxt(g), 'n = ' + this.nTxt(g) + (this.wenig(g) ? ' (wenig Daten)' : '')]);
          svg.append(hit);
        });
        sec.append(h('figure', { class: 'thct-fig' }, svg, h('figcaption', { class: 'thct-klein', text: 'Punkt: Mittelwert (hohl: unter 30 Teilnahmen, dann ohne Verbindungslinie), Strich: 95-%-Bereich, n unter der Achse. Alle Werte auf der gemeinsamen Erwachsenen-Skala, nicht altersnormiert.' })));
      }
      sec.append(this.tabelle('Werte nach Alter', ['Alter', 'n', 'Mittelwert', '95-%-Bereich'], this.zeilen(groups)));
      return sec;
    }

    verteilung(d) {
      const sec = h('section', { class: 'thct-stat', 'aria-labelledby': 'thct-st-v' }, h('h3', { id: 'thct-st-v', text: 'Verteilung aller Ergebnisse' }));
      if (!d.verteilung) { sec.append(this.leer(null, '')); return sec; }
      const W = this.chartW(), H = 230, L = 36, R = 16, T = 12, B = 190;
      const x = (v) => L + ((v - 55) / 90) * (W - L - R);
      const maxS = Math.max(...d.verteilung.map((b) => b.share), (normalPdf(100, 100, 15) * 5));
      const y = (v) => B - (v / maxS) * (B - T);
      const svg = s('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'thct-hist', role: 'img', 'aria-label': 'Histogramm der Ergebnisse in 5-Punkt-Stufen mit Gauß-Kurve zum Vergleich' });
      for (const t of [70, 85, 100, 115, 130]) svg.append(s('text', { x: x(t), y: B + 18, 'text-anchor': 'middle', class: 'thct-achsentext' }, String(t)));
      for (const b of d.verteilung) {
        const bx = x(b.from) + 1, bw = x(b.to) - x(b.from) - 2;
        const r = s('rect', { x: bx, y: y(b.share), width: bw, height: Math.max(0, B - y(b.share)), rx: 3, fill: '#3A6AAE', tabindex: '0', 'aria-label': b.from + ' bis ' + b.to + ': ' + NF0.format(b.share * 100) + ' %' });
        this.bindTip(r, [b.from + ' bis unter ' + b.to, NF0.format(b.share * 100) + ' % der Gewerteten']);
        svg.append(r);
      }
      let dn = '';
      for (let v = 55; v <= 145; v += 1) dn += (v === 55 ? 'M' : 'L') + x(v).toFixed(1) + ',' + y(normalPdf(v, 100, 15) * 5).toFixed(1);
      svg.append(s('path', { d: dn, fill: 'none', stroke: '#5E6E80', 'stroke-width': 1.5 }));
      svg.append(s('line', { x1: L, x2: W - R, y1: B, y2: B, class: 'thct-achse' }));
      sec.append(h('figure', { class: 'thct-fig' }, h('ul', { class: 'thct-legende' }, h('li', null, h('i', { style: 'background:#3A6AAE' }), 'Anteil der Gewerteten je 5-Punkt-Stufe (n = ' + NF0.format(d.inStats) + ')'), h('li', null, h('i', { class: 'is-linie', style: 'background:#5E6E80' }), 'Gauß-Kurve 100 ± 15 (Bevölkerung, theoretisch)')), svg));
      sec.append(this.tabelle('Verteilung', ['Stufe', 'Anteil'], d.verteilung.map((b) => [b.from + ' bis unter ' + b.to, NF0.format(b.share * 100) + ' %'])));
      return sec;
    }
  }

  customElements.define('th-cogtest', ThCogtest);
  customElements.define('th-cogtest-stats', ThCogtestStats);
})();
